import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import Global from "../plugins/global";
import { encryptData, decryptData } from '../helpers/crypto'
import swal from 'sweetalert2';

let isRefreshing = false;
let failedQueue = [];
let autoRefreshTimer = null;
let lastActivityTime = Date.now();
var IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes — user considered "active" if interaction within this window

function processQueue(error, token = null) {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
}
function getAxiosErrorMessage(error, fallbackMessage) {
  if (
    error &&
    error.response &&
    error.response.data &&
    error.response.data.message
  ) {
    return error.response.data.message;
  }

  if (error && error.message) {
    return error.message;
  }

  return fallbackMessage;
}

function updateActivityTime() {
  lastActivityTime = Date.now();
}

var activityThrottleTimer = null;
function throttledActivityUpdate() {
  if (activityThrottleTimer) return;
  activityThrottleTimer = setTimeout(function() {
    activityThrottleTimer = null;
  }, 30000); // throttle: update at most once per 30 seconds
  // Detect return from idle — proactively refresh token if it may have expired
  var wasIdle = (Date.now() - lastActivityTime) > IDLE_TIMEOUT;
  updateActivityTime();
  if (wasIdle && store && store.getters.loggedIn) {
    checkAndRefreshToken();
  }
}

// Register activity listeners (passive for scroll performance)
if (typeof document !== 'undefined') {
  ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach(function(event) {
    document.addEventListener(event, throttledActivityUpdate, { passive: true });
  });

  // Detect tab/window visibility change (handles laptop sleep, tab switch, minimize/restore)
  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'visible' && store && store.getters.loggedIn) {
      checkAndRefreshToken();
    }
  });
}


Vue.use(Vuex);
Vue.use(Global);
//axios.defaults.baseURL = 'http://data.exactmatchmarketing.local/api'
//axios.defaults.baseURL = 'https://databeta.exactmatchmarketing.com/api'
axios.defaults.baseURL = process.env.VUE_APP_DATASERVER_URL + "/api";

axios.interceptors.response.use(
  response => response,
  async error => {
    if (error.response && error.response.status === 401 && !error.config._retry) {
      // If no token, user wasn't authenticated — handle gracefully
      if (!localStorage.getItem('access_token')) {
        if (window.location.pathname.indexOf('/login') === -1) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('expires_in');
          localStorage.removeItem('token_expires_at');
          localStorage.removeItem('userData');
          localStorage.removeItem('userDataOri');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      const originalRequest = error.config;
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return axios(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        // No body needed — refresh_token is sent automatically via HttpOnly cookie
        // _retry: true prevents this request's own 401 from re-entering the interceptor (deadlock prevention)
        const res = await axios.post('/auth/refresh-token', null, { _retry: true, withCredentials: true });

        const { access_token, expires_in } = res.data;

        localStorage.setItem('access_token', access_token);
        localStorage.setItem('expires_in', expires_in);
        localStorage.setItem('token_expires_at', String(Date.now() + (expires_in * 1000)));
        store.commit("retrieveToken", access_token);
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;

        processQueue(null, access_token);
        isRefreshing = false;
        setupAutoRefresh();

        originalRequest.headers['Authorization'] = 'Bearer ' + access_token;
        return axios(originalRequest);
      } catch (err) {
        // Cross-tab race condition: another tab may have just rotated the refresh_token cookie.
        // Wait briefly for the browser to receive the other tab's Set-Cookie, then retry once.
        try {
          await new Promise(function(resolve) { setTimeout(resolve, 1500); });
          var retryRes = await axios.post('/auth/refresh-token', null, { _retry: true, withCredentials: true });

          var retryToken = retryRes.data.access_token;
          var retryExpiresIn = retryRes.data.expires_in;

          localStorage.setItem('access_token', retryToken);
          localStorage.setItem('expires_in', retryExpiresIn);
          localStorage.setItem('token_expires_at', String(Date.now() + (retryExpiresIn * 1000)));
          store.commit("retrieveToken", retryToken);
          axios.defaults.headers.common['Authorization'] = 'Bearer ' + retryToken;

          processQueue(null, retryToken);
          isRefreshing = false;
          setupAutoRefresh();

          originalRequest.headers['Authorization'] = 'Bearer ' + retryToken;
          return axios(originalRequest);
        } catch (retryErr) {
          // Retry also failed — refresh_token is genuinely expired/invalid
          isRefreshing = false;
          processQueue(retryErr, null);
          stopAutoRefresh();

          setTimeout(function() {
            var container = document.querySelector('.swal2-container');
            var popup = document.querySelector('.swal2-popup');
            if (container) container.style.zIndex = '99998';
            if (popup) popup.style.zIndex = '99999';
          }, 50);

          swal.fire({
            title: 'Session Ended',
            text: 'Your session has ended. Please log in again to continue.',
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(function() {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('expires_in');
            localStorage.removeItem('token_expires_at');
            localStorage.removeItem('userData');
            localStorage.removeItem('userDataOri');
            sessionStorage.removeItem('userData');
            sessionStorage.removeItem('userDataOri');
            sessionStorage.removeItem('userDataAgency');
            sessionStorage.removeItem(VIEW_AS_ACTIVE_KEY);
            window.location.href = '/login';
          });

          return Promise.reject(retryErr);
        }
      }
    }
    return Promise.reject(error);
  }
);

var global = Vue.prototype.$global;
var _clientIP = localStorage.getItem("clientIP");

function fetchClientIP() {
  fetch("https://www.cloudflare.com/cdn-cgi/trace")
    .then(response => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.text();
    })
    .then(data => {
      const lines = data.split("\n");
      const details = {};
      lines.forEach(line => {
        const [key, value] = line.split("=");
        details[key] = value;
      });

      // Accessing the client's IP address
      const clientIPAddress = details["ip"];
      localStorage.setItem("clientIP", clientIPAddress);
      axios.interceptors.request.use(
        function(config) {
          // Add "clientip" header to the request
          config.headers["clientip"] =
            clientIPAddress +
            "|" +
            Intl.DateTimeFormat().resolvedOptions().timeZone; // Replace with the actual client IP
          config.headers[
            "cltmz"
          ] = Intl.DateTimeFormat().resolvedOptions().timeZone;
          return config;
        },
        function(error) {
          return Promise.reject(error);
        }
      );
    })
    .catch(error => {
      console.error("Error:", error);
    });
}

if (_clientIP === null || _clientIP == "") {
  fetchClientIP();
} else {
  axios.interceptors.request.use(
    function(config) {
      // Add "clientip" header to the request
      config.headers["clientip"] =
        _clientIP + "|" + Intl.DateTimeFormat().resolvedOptions().timeZone; // Replace with the actual client IP
      config.headers[
        "cltmz"
      ] = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return config;
    },
    function(error) {
      return Promise.reject(error);
    }
  );
}

const VIEW_AS_ACTIVE_KEY = 'viewAsActive';

/** Prefer sessionStorage when in view-as (per-tab state, avoids multi-tab overwrite). */
function getStoredUserData(storage) {
  const storedData = storage.getItem("userData");
  if (!storedData) return null;
  try {
    const parsedData = JSON.parse(storedData);
    if ((parsedData.systemuser && parsedData.rootcomp) || parsedData.isViewMode) {
      return parsedData;
    }
  } catch (_) {}
  try {
    return decryptData(storedData);
  } catch (_) {
    return null;
  }
}

function getUserDataFromLocalStorage() {
  // View-as state is per-tab in sessionStorage
  if (sessionStorage.getItem('userDataOri') || sessionStorage.getItem('userDataAgency')) {
    const data = getStoredUserData(sessionStorage);
    if (data) return data;
  }
  const storedData = localStorage.getItem("userData");
  if (!storedData) return {};
  try {
    const parsedData = JSON.parse(storedData);
    if ((parsedData.systemuser && parsedData.rootcomp) || parsedData.isViewMode) {
      return parsedData;
    } else {
      localStorage.removeItem('access_token');
      localStorage.removeItem('userData');
      window.location.href = '/login';
    }
  } catch (error) {
    // If parsing fails, it means the data is probably encrypted
  }
  return decryptData(storedData);
}

function setupAutoRefresh() {
  if (autoRefreshTimer) {
    clearTimeout(autoRefreshTimer);
    autoRefreshTimer = null;
  }

  var expiresIn = parseInt(localStorage.getItem('expires_in') || '7200'); // seconds
  var refreshBefore = 5 * 60 * 1000; // 5 minutes before expiry
  var refreshTime = (expiresIn * 1000) - refreshBefore;
  if (refreshTime < 60000) refreshTime = 60000; // minimum 1 minute

  autoRefreshTimer = setTimeout(function() {
    autoRefreshTimer = null;

    if (!localStorage.getItem('access_token')) return; // not logged in
    if (isRefreshing) return;
    isRefreshing = true;

    axios.post('/auth/refresh-token', null, { _retry: true, withCredentials: true })
      .then(function(res) {
        var access_token = res.data.access_token;
        var expires_in = res.data.expires_in;

        localStorage.setItem('access_token', access_token);
        localStorage.setItem('expires_in', expires_in);
        localStorage.setItem('token_expires_at', String(Date.now() + (expires_in * 1000)));
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
        store.commit('retrieveToken', access_token);

        isRefreshing = false;
        processQueue(null, access_token);
        setupAutoRefresh(); // schedule next refresh
      })
      .catch(function(err) {
        isRefreshing = false;
        processQueue(err, null);
        // Don't clear storage or redirect — let 401 interceptor handle on next user request
      });
  }, refreshTime);
}

function stopAutoRefresh() {
  if (autoRefreshTimer) {
    clearTimeout(autoRefreshTimer);
    autoRefreshTimer = null;
  }
}

/**
 * Proactively check if token is expired/near-expired and refresh.
 * Called when: user returns from idle, tab becomes visible, page loads.
 * Catches scenarios where setupAutoRefresh timer couldn't fire
 * (laptop sleep, browser throttled background tabs, timer died).
 */
function checkAndRefreshToken() {
  if (!localStorage.getItem('access_token')) return;
  if (isRefreshing) return;

  var expiresAt = parseInt(localStorage.getItem('token_expires_at') || '0');
  if (expiresAt === 0) {
    // No expiry timestamp (e.g., user logged in before this code was deployed).
    // Token may already be expired — proactively refresh to be safe.
    isRefreshing = true;
    axios.post('/auth/refresh-token', null, { _retry: true, withCredentials: true })
      .then(function(res) {
        var access_token = res.data.access_token;
        var expires_in = res.data.expires_in;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('expires_in', expires_in);
        localStorage.setItem('token_expires_at', String(Date.now() + (expires_in * 1000)));
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
        store.commit('retrieveToken', access_token);
        isRefreshing = false;
        processQueue(null, access_token);
        setupAutoRefresh();
      })
      .catch(function(err) {
        isRefreshing = false;
        processQueue(err, null);
        // Token might still be valid — start timer and let 401 interceptor handle if needed
        if (!autoRefreshTimer) setupAutoRefresh();
      });
    return;
  }

  var now = Date.now();
  var bufferMs = 60 * 1000; // 1 minute buffer

  if (now >= expiresAt - bufferMs) {
    // Token expired or about to expire — proactive refresh
    isRefreshing = true;

    axios.post('/auth/refresh-token', null, { _retry: true, withCredentials: true })
      .then(function(res) {
        var access_token = res.data.access_token;
        var expires_in = res.data.expires_in;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('expires_in', expires_in);
        localStorage.setItem('token_expires_at', String(Date.now() + (expires_in * 1000)));
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
        store.commit('retrieveToken', access_token);
        isRefreshing = false;
        processQueue(null, access_token);
        setupAutoRefresh();
      })
      .catch(function(err) {
        isRefreshing = false;
        processQueue(err, null);
        // Don't force logout — let 401 interceptor handle on next user API call
      });
  } else {
    // Token still valid — ensure auto-refresh timer is running
    if (!autoRefreshTimer) {
      setupAutoRefresh();
    }
  }
}

export const store = new Vuex.Store({
  state: {
    token: localStorage.getItem("access_token") || null,
    userData: getUserDataFromLocalStorage(),
    isRefreshing: false,
    resultData: {},
    agreements: [],
    agreementLoading: false,
    registeredPlaceholders: {},
  },
  getters: {
    loggedIn(state) {
      return state.token !== null;
    },
    userCompletedSetup(state) {
      return state.userData.profile_setup_completed !== null;
    },
    userData(state) {
      return state.userData;
    },
    resultData(state) {
      return state.resultData;
    },
    getUserType(state) {
      return state.userData.user_type;
    },
    agreements(state) {
      return state.agreements;
    },
    agreementLoading(state) {
      return state.agreementLoading;
    },
    registeredPlaceholders(state) {
      return state.registeredPlaceholders;
    },
    agreementsByType: (state) => (type) => {
      return state.agreements.filter(agreement => agreement.name === type);
    },
    activeAgreementByType: (state) => (type) => {
      return state.agreements.find(
        agreement => agreement.name === type && agreement.status === 'active'
      ); 
    },
  },
  mutations: {
    retrieveToken(state, token) {
      state.token = token;
    },
    destroyToken(state) {
      state.token = null;
      state.userData = null;
    },
    retrieveUser(state, data) {
      state.userData = data;
      const isViewAs = sessionStorage.getItem(VIEW_AS_ACTIVE_KEY);
      if (isViewAs) {
        if ((data.systemuser && data.rootcomp) || data.isViewMode) {
          sessionStorage.setItem("userData", JSON.stringify(data));
        } else {
          sessionStorage.setItem("userData", encryptData(data));
        }
        return;
      }
      if ((data.systemuser && data.rootcomp) || data.isViewMode) {
        return localStorage.setItem("userData", JSON.stringify(data));
      }

      const encryptedData = encryptData(data);
      localStorage.setItem("userData", encryptedData);
    },
    putResultData(state, data) {
      state.resultData = data;
    },
    updateUserData(state, newData) {
      let userData = state.userData || {};
      userData = { ...userData, ...newData };
      state.userData = userData;

      const isViewAs = sessionStorage.getItem(VIEW_AS_ACTIVE_KEY);
      if (isViewAs) {
        if ((userData.systemuser && userData.rootcomp) || userData.isViewMode) {
          sessionStorage.setItem("userData", JSON.stringify(userData));
        } else {
          sessionStorage.setItem("userData", encryptData(userData));
        }
        return;
      }
      if ((userData.systemuser && userData.rootcomp) || userData.isViewMode) {
        return localStorage.setItem("userData", JSON.stringify(userData));
      }

      const encryptedData = encryptData(userData);
      localStorage.setItem('userData', encryptedData);
    },
    setAgreements(state, agreements) {
      state.agreements = agreements;
    },
    setAgreementLoading(state, loading) {
      state.agreementLoading = loading;
    },
    setRegisteredPlaceholders(state, { type, placeholders }) {
      state.registeredPlaceholders = {
        ...state.registeredPlaceholders,
        [type]: placeholders,
      };
    },
    addAgreement(state, agreement) {
      state.agreements.push(agreement);
    },
    updateAgreement(state, updatedAgreement) {
      const index = state.agreements.findIndex(a => a.id === updatedAgreement.id);
      if (index !== -1) {
        state.agreements.splice(index, 1, updatedAgreement);
      }
    },
    updateAgreementsStatus(state, { type, activeId }) {
      state.agreements = state.agreements.map(agreement => {
        if (agreement.name === type) {
          return {
            ...agreement,
            status: agreement.id === activeId ? 'active' : 'inactive',
          };
        }
        return agreement;
      });
    },
  },
  actions: {
    updateUserData({ commit }, newData) {
      commit('updateUserData', newData);
    },
    setLogout({ commit }) {
      stopAutoRefresh();
      commit('destroyToken');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('expires_in');
      localStorage.removeItem('token_expires_at');
      localStorage.removeItem('userData');
      localStorage.removeItem("userDataOri");
      sessionStorage.removeItem('userData');
      sessionStorage.removeItem('userDataOri');
      sessionStorage.removeItem('userDataAgency');
      sessionStorage.removeItem(VIEW_AS_ACTIVE_KEY);
      window.location.href = '/login'
    },
    fetchUserFromLocalStorage() {
      this.state.userData = getUserDataFromLocalStorage();
    },
    async fetchAgreements({ commit, state }, filters = {}) {
      commit('setAgreementLoading', true);

      try {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token;

        const params = {};
        if (filters.type) params.type = filters.type;
        if (filters.status) params.status = filters.status;
        if (filters.include_archived) params.include_archived = 1;

        const response = await axios.get('/admin/agreements', { params });

        if (response.data.result === 'success') {
          commit('setAgreements', response.data.data);
          return response.data.data;
        }
        throw new Error(response.data.message || 'Failed to fetch agreements');
      } catch (error) {
        console.error('[fetchAgreements]', error);
        throw getAxiosErrorMessage(error, 'Failed to fetch agreements');
      } finally {
        commit('setAgreementLoading', false);
      }
    },
    async uploadAgreement({ commit, state }, formData) {
      commit('setAgreementLoading', true);

      try {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token;

        const response = await axios.post('/admin/agreements', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data.result === 'success') {
          commit('addAgreement', response.data.data);
          return response.data;
        }
        throw new Error(response.data.message || 'Failed to upload agreement');
      } catch (error) {
        console.error('[uploadAgreement]', error);
        throw getAxiosErrorMessage(error, 'Failed to upload agreement');
      } finally {
        commit('setAgreementLoading', false);
      }
    },
    /**
     * Fetch agreement HTML body for editing (always creates a new version on save; never mutates this file).
     */
    async fetchAgreementHtmlContent({ state }, id) {
      try {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token;
        const response = await axios.get(`/admin/agreements/${id}/download`, {
          responseType: 'text',
        });
        if (typeof response.data === 'string') {
          return response.data;
        }
        throw new Error('Unexpected response when loading agreement HTML');
      } catch (error) {
        console.error('[fetchAgreementHtmlContent]', error);
        throw new Error(getAxiosErrorMessage(error, 'Failed to load agreement HTML'));
      }
    },
    /**
     * Save a new agreement version from raw HTML (JSON body). Does not use global agreement list loading.
     */
    async uploadAgreementFromHtml({ state }, payload) {
      try {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token;
        const response = await axios.post('/admin/agreements', {
          agreement_type: payload.agreement_type,
          html_content: payload.html_content,
          description: payload.description || '',
          source_mode: payload.source_mode || 'manual',
        });
        if (response.data.result === 'success') {
          return response.data;
        }
        throw new Error(response.data.message || 'Failed to save agreement');
      } catch (error) {
        console.error('[uploadAgreementFromHtml]', error);
        throw new Error(getAxiosErrorMessage(error, 'Failed to save agreement'));
      }
    },
    async activateAgreement({ commit, state }, id) {
      commit('setAgreementLoading', true);

      try {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token;
        const response = await axios.post(`/admin/agreements/${id}/activate`);

        if (response.data.result === 'success') {
          const { name } = response.data.data;
          commit('updateAgreementsStatus', { type: name, activeId: id });
          return response.data;
        }
        throw new Error(response.data.message || 'Failed to activate agreement');
      } catch (error) {
        console.error('[activateAgreement]', error);
        throw getAxiosErrorMessage(error, 'Failed to activate agreement');
      } finally {
        commit('setAgreementLoading', false);
      }
    },
    async deactivateAgreement({ commit, state }, id) {
      commit('setAgreementLoading', true);

      try {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token;
        const response = await axios.post(`/admin/agreements/${id}/deactivate`);

        if (response.data.result === 'success') {
          const existing = state.agreements.find((a) => a.id === id);
          if (existing) {
            commit('updateAgreement', { ...existing, status: 'inactive' });
          }
          return response.data;
        }
        throw new Error(response.data.message || 'Failed to deactivate agreement');
      } catch (error) {
        console.error('[deactivateAgreement]', error);
        throw getAxiosErrorMessage(error, 'Failed to deactivate agreement');
      } finally {
        commit('setAgreementLoading', false);
      }
    },
    async archiveAgreement({ state }, id) {
      try {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token;
        const response = await axios.post(`/admin/agreements/${id}/archive`);
        if (response.data.result === 'success') {
          return response.data;
        }
        throw new Error(response.data.message || 'Failed to archive agreement');
      } catch (error) {
        console.error('[archiveAgreement]', error);
        throw getAxiosErrorMessage(error, 'Failed to archive agreement');
      }
    },
    async restoreAgreement({ state }, id) {
      try {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token;
        const response = await axios.post(`/admin/agreements/${id}/restore`);
        if (response.data.result === 'success') {
          return response.data;
        }
        throw new Error(response.data.message || 'Failed to restore agreement');
      } catch (error) {
        console.error('[restoreAgreement]', error);
        throw getAxiosErrorMessage(error, 'Failed to restore agreement');
      }
    },
    async downloadAgreement({ state }, id) {
      try {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token;

        const response = await axios.get(`/admin/agreements/${id}/download`, {
          responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers['content-disposition'];
        let filename = 'agreement.html';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (error) {
        console.error('[downloadAgreement]', error);
        throw getAxiosErrorMessage(error, 'Failed to download agreement');
      }
    },
    async getRegisteredPlaceholders({ commit, state }, type) {
      try {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token;

        const response = await axios.get('/admin/agreements/placeholders', {
          params: { type },
        });

        if (response.data.result === 'success') {
          commit('setRegisteredPlaceholders', {
            type,
            placeholders: response.data.placeholders,
          });
          return response.data.placeholders;
        }
        throw new Error(response.data.message || 'Failed to get placeholders');
      } catch (error) {
        console.error('[getRegisteredPlaceholders]', error);
        throw getAxiosErrorMessage(error, 'Failed to get placeholders');
      }
    },
    getMinimumValueSimplifi(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get('/configuration/setting/minvaluesimplifi', {
          params: {
            idsys: data.idsys
          }
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error)
        })
      });
    },
    processChargeCampaignByRemainingDays(context, data) {
      return new Promise((resolve, reject) => {
      	axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/leadspeek/simplifi/charge-campaign-by-remaining-days', {
          companyID: data.companyID,
          leadspeekID: data.leadspeekID,
          userID: data.LeadspeekUserId,
          leadspeekMaxBid: data.leadspeekMaxBid,
          leadspeekMonthlyBudget: data.leadspeekMonthlyBudget,
          leadspeekDailyBudget: data.leadspeekDailyBudget,
          leadspeekGoalValue: data.leadspeekGoalValue,
          leadspeekGoalType: data.leadspeekGoalType,
          leadspeekAgencyMarkup: data.leadspeekAgencyMarkup,
          leadspeekRemainingDays: data.leadspeekRemainingDays,
          leadspeekTotalSpend: data.leadspeekTotalSpend,
          costClientByRemainingDays: data.costClientByRemainingDays,
          costAgencyByRemainingDays: data.costAgencyByRemainingDays,
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    checkBudgetSimplifiIncrease(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get(`/leadspeek/simplifi/check-budget-increase`, {
          params: {
            campaign_action_source: data.campaign_action_source,
            leadspeek_api_id: data.leadspeek_api_id,
            current_daily_budget: data.current_daily_budget,
            current_agency_markup: data.current_agency_markup,
            current_goal_type: data.current_goal_type,
            current_goal_value: data.current_goal_value,
          }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    getRemainingBudgetSimplifi(context, data) {
      return new Promise((resolve, reject) => {
        axios.get(`/leadspeek/simplifi/remaining-budget/${data.leadspeek_api_id}`)
             .then(response => {
              resolve(response.data)
             })
             .catch(error => {
              reject(error)
             })
      });
    },
    SimplifiModuleCost(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.put('/leadspeek/simplifi/costmodule', {
          ClientID: data.ClientID,
          LeadspeekMaxBid: data.LeadspeekMaxBid,
          LeadspeekMonthlyBudget: data.LeadspeekMonthlyBudget,
          LeadspeekDailyBudget: data.LeadspeekDailyBudget,
          LeadspeekGoalValue: data.LeadspeekGoalValue,
          LeadspeekGoalType: data.LeadspeekGoalType,
          LeadspeekAgencyMarkup: data.LeadspeekAgencyMarkup,
          LeadspeekAgencyCoversCost: data.LeadspeekAgencyCoversCost,
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    ghlv2GetListSubAccountsAll(context, data) {
      return new Promise((resolve, reject) => {
      	axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get(`/gohighlevelv2/list-subaccounts-all/${data.company_id}`)
            .then(response => {
              resolve(response.data);
            })
            .catch(error => {
              reject(error);
            })
      })
    },
    createClientFromSubAccountLeadConnector(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
      	axios.post('/gohighlevelv2/create-client-from-subaccount', {
          company_id: data.company_id,
          sub_accounts: data.sub_accounts,
          user_ip: data.user_ip
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    ghlv2GetListSubAccounts(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get(`/gohighlevelv2/list-subaccounts/${data.company_id}`, {
          params: {
            page: data.page,
          }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    
    createIframeGhlv2Client(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/gohighlevelv2/create-iframe/client', {
          company_id: data.company_id,
          company_parent: data.company_parent,
          custom_menu_name: data.custom_menu_name,
          user_ip: data.user_ip,
          location_id: data.location_id,
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      })
    },
    deleteIframeGhlv2Client(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.delete('/gohighlevelv2/delete-iframe/client', {
          data: {
            company_id: data.company_id,
            company_parent: data.company_parent,
            user_ip: data.user_ip,
          }
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error);
        })
      });
    },

    createIframeGhlv2ClientYourAccount(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/gohighlevelv2/create-iframe/client-your-account', {
          company_id: data.company_id,
          subdomain_url: data.subdomain_url,
          enable_sendgrid: data.enable_sendgrid,
          custom_fields_general: data.custom_fields_general,
          custom_fields_b2b: data.custom_fields_b2b,
          custom_fields_advance: data.custom_fields_advance,
          custom_menu_name: data.custom_menu_name,
          user_ip: data.user_ip,
          is_in_iframe: data.is_in_iframe
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      })
    },
    deleteIframeGhlv2ClientYourAccount(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.delete('/gohighlevelv2/delete-iframe/client-your-account', {
          data: {
            company_id: data.company_id,
            user_ip: data.user_ip,
          }
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error);
        })
      });
    },

    checkGohighlevelConnect(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
      	axios.get(`/general/check-connected-gohighlevel/${data.companyID}`)
             .then(response => {
              resolve(response.data);
             })
             .catch(error => {
              reject(error);
             })
      });
    },
    getMinimumSpendUser(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get('/general/minimum-spend-user', {
          params: {
            company_id: data.company_id
          }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    getMinimumSpendList(context, data) {
      return new Promise((resolve, reject) => {
      	axios.get('/general/minimum-spend-config', {
          params: {
            page: data.page
          }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      })
    },
    createMinimumSpendConfig(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/general/minimum-spend-config', {
          planName: data.planName,
          months: data.months,
          flatMonth: data.flatMonth,
          ipLogin: data.ipLogin,
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })        
      });
    },
    updateMinimumSpendConfig(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.put('/general/minimum-spend-config', {
          idMinimumSpend: data.idMinimumSpend,
          planName: data.planName,
          months: data.months,
          flatMonth: data.flatMonth,
          ipLogin: data.ipLogin,
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    deleteMinimumSpendConfig(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.delete('/general/minimum-spend-config', {
          data: {
            idMinimumSpend: data.idMinimumSpend,
            ipLogin: data.ipLogin,
          }
        })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        })
      })
    },
    setDefaultMinimumSpendConfig(context, data) {
      return new Promise((resolve, reject) => {
        axios.put('/general/set-default-minimum-spend-config', {
          id: data.id,
          ipLogin: data.ipLogin,
        })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    estimateRefundCampaignPrepaid(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get('/leadspeek/client/estimeate-refund-topup', {
          params: {
            leadspeek_api_id: data.leadspeek_api_id
          }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    refundCampaignPrepaid(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/leadspeek/client/refund-topup', {
          leadspeek_api_id: data.leadspeek_api_id,
          company_id: data.company_id,
          ip_user: data.ip_user,
          timezone: data.timezone,
          payment_type: data.payment_type,
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    onSaveUserLogs(context, data){
        const userData = context.getters.userData;
        
        
        let payload = {
          userID: userData.id,
          action: data.action,
          desc: data.desc,
          userIP: userData.ip_login,
          target_userID: data.target_id,
        }

      const userViewMode = userData.isViewMode ? userData.isViewMode : false;
      const globalviewmode = global.globalviewmode ? global.globalviewmode : false;
      if(userViewMode || globalviewmode){
          const rawOri = sessionStorage.getItem('userDataOri') || localStorage.getItem('userDataOri');
          if (rawOri) {
            const userRoot = decryptData(rawOri);
            payload.userID = userRoot.id;
          }
      }
      
      context.dispatch('userLogSaved', { data: payload });
    },
    getCurrentTopupPrepaid(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get(`/leadspeek/client/local/currenttopup/${data.leadspeek_api_id}`)
             .then(response => {
              resolve(response.data);
             })
             .catch(error => {
              reject(error);
             })
      })
    },
    getPrepaidDirectPayment(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get('/configuration/agencies/prepaid/get-direct-payment', {
          params: {
            company_id: data.company_id,
            date: data.date
          }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    getTransactionHistoryDirectPayment(context, data) {
      return new Promise((resolve, reject) => {
      	axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get('/configuration/agencies/prepaid/get-transaction-history-direct-payment', {
          params: {
            company_id: data.company_id,
            date: data.date
          }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error)
        })
      });
    },
    stopContinualTopupDirectPayment(context, data) {
      return new Promise((resolve, reject) => {
      	axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.put('/configuration/agencies/prepaid/stop-continual-topup', {
          company_id: data.company_id,
          stop_continual: data.stop_continual,
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    savePrepaidDirectPayment(context, data) {
      return new Promise((resolve, reject) => {
      	axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/configuration/agencies/prepaid/save-prepaid-direct-payment', {
          company_id: data.company_id,
          amount: data.amount,
          custom_amount: data.custom_amount,
          stop_continual: data.stop_continual,
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    chargePrepaidDirectPayment(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/configuration/agencies/prepaid/charge-prepaid-direct-payment', {
          company_id: data.company_id,
          amount: data.amount,
          custom_amount: data.custom_amount,
          stop_continual: data.stop_continual,
          ip_user: data.ip_user,
          timezone: data.timezone,
          payment_type: data.payment_type,
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    GetListAdvanceInformation(context, data) {
      return new Promise((resolve, reject) => {
        axios.get('/configuration/list-advance-information')
             .then(response => {
              resolve(response.data);
             })
             .catch(error => {
              reject(error);
             })
      });
    },
    GetListAdvanceInformationB2B(context, data) {
      return new Promise((resolve, reject) => {
        axios.get('/configuration/list-advance-information-b2b')
             .then(response => {
              resolve(response.data);
             })
             .catch(error => {
              reject(error);
             })
      });
    },
    GetListAdvanceInformationByType(context, data) {
      return new Promise((resolve, reject) => {
        axios.get('/configuration/list-advance-information-type', {
              params: {
               type: data.type, 
              }
            })
             .then(response => {
              resolve(response.data);
             })
             .catch(error => {
              reject(error);
             })
      });
    },
    generateAuthenticateOpenApi(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/v1/developer/create/userapi', {
          companyID: data.companyID,
          email: data.email,
        })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    getAuthenticateOpenApi(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get(`/v1/developer/userapi/${data.companyID}`)
             .then(response => {
              resolve(response);
             })
             .catch(error => {
              reject(error);
             })
      });
    },
    getClientMinLeadDayEnhance(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get('/configuration/setting/minleaddayenhance', {
          params: {
            idSys: data.idSys
          }
        })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    purgeOptoutList(context, data) {
      return new Promise((resolve, reject) => {
      	axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.delete(`/tools/optout/purge/${data.companyRootId}`)
             .then(response => {
              resolve(response);
             })
             .catch(error => {
              reject(error);
             })
      })
    },
    jobProgress(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get(`/leadspeek/suppressionlist/progress?leadspeekID=${data.leadspeekID}&companyId=${data.companyId}&leadspeekApiId=${data.leadspeekApiId}&campaignType=${data.campaignType}`)
             .then(response => {
              resolve(response);
             })
             .catch(error => {
              reject(error);
             })
      });
    },
    stopContinualTopUp(context, data) {
      return new  Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.put('/leadspeek/client/local/stopcontinualtopup', {
          leadspeek_api_id: data.leadspeek_api_id,
          lp_limit_freq: data.lp_limit_freq
        })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    getRemainingBalanceLeads(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get(`/leadspeek/client/local/remainingbalanceleads/${data.leadspeek_api_id}`)
            .then(response => {
              resolve(response);
            })
            .catch(error => {
              reject(error);
            })
      });
    },
    /** GENERATE REF LINK */
    CheckRefLink(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/general/referralink/check/" + data.refcode)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    CreateRefLink(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/general/referralink/create", {
            userID: data.userID
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /** GENERATE REF LINK */

    /** CANCEL SUBSCRIPTION */
    cancelsubscription(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/configuration/cancel-subscription", {
            companyID: data.companyID
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /** CANCEL SUBSCRIPTION */

    /** MANUAL AGENCY BILLING */
    agencyManualBill(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/payment/set-billing-method", {
            companyID: data.companyID,
            manualBill: data.manualBill
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /** MANUAL AGENCY BILLING */

    /** GOHIGHLEVEL */
    getGhlUserTags(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/gohighlevel/usertags/" + data.campaignID + "/" + data.companyID, {
              params: {
                ip_user: data.userIP
              }
            }
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getGoHighLevelTags(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/gohighlevel/tags/" + data.companyID)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /** GOHIGHLEVEL */

    /* MAILBOXPOWER */
    getMailBoxPowerGroups(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/mailboxpower/groups/" + data.campaignID + "/" + data.companyID)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getMailBoxPowerUserGroups(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/mailboxpower/usergroups/" + data.campaignID + "/" + data.companyID)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /* MAILBOXPOWER */

    /** ARCHIVE CAMPAIGN */
    archiveCampaign(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/leadspeek/client/archive", {
            lpuserid: data.lpuserid,
            status: data.status
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /** ARCHIVE CAMPAIGN */
    /** COMPANY GROUP */
    RemoveCompanyGroup(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .delete("/company/group/remove/" + data.companyGroupID)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    AddEditGroupCompany(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/company/group", {
            companyGroupID: data.companyGroupID,
            companyGroupName: data.companyGroupName,
            companyID: data.companyID,
            moduleID: data.moduleID
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    GetCompanyGroup(context, data) {
      return new Promise((resolve, reject) => {
        var _companyID = "";
        var _userModule = "";

        if (data.companyID != "") {
          _companyID = data.companyID;
        }
        if (
          data.companyID != "" &&
          data.userModule != "" &&
          typeof data.userModule != "undefined"
        ) {
          _userModule = "/" + data.userModule;
        }

        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/company/group/" + _companyID + _userModule)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /** COMPANY GROUP */

    /** PAYMENT CUSTOMER */
    processUpdateCard(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/payment/card", {
            tokenid: data.tokenid,
            cardholder: data.cardholder,
            address: data.address,
            city: data.city,
            state: data.state,
            country: data.country,
            zip: data.zip,
            usrID: data.usrID,
            companyParentID: data.companyParentID,
            action: data.action,
            document_type: data.document_type,
            ip_user: data.ip_user,
            timezone: data.timezone,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    clearPaymentStatus(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/payment/clear-payment-status", {
            usrID: data.usrID,
            companyParentID: data.companyParentID,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getCardInfo(context, data) {
      return new Promise((resolve, reject) => {
        var _userID = "";

        if (
          typeof data != "undefined" &&
          data.usrID != "" &&
          typeof data.usrID != "undefined"
        ) {
          _userID = "/" + data.usrID;
        }

        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/payment/card" + _userID)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    paymentcustomersetup(context, data) {
      return new Promise((resolve, reject) => {
        const payload = {
          tokenid: data.tokenid,
          cardholder: data.cardholder,
          address: data.address,
          city: data.city,
          state: data.state,
          country: data.country,
          zip: data.zip,
          usrID: data.usrID,
          action: data.action,
          amount: data.amount || '',
          document_type: data.document_type,
          from: data.from || '',
        };

        if (data.code && data.code.trim() !== '') {
          payload.code = data.code;
        }

        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/payment/customer", payload)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    paymentdirectcustomersetup(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/payment/customer-direct", {
            usrID: data.usrID
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /** PAYMENT CUSTOMER */

    /** LEADSPEEK */
    /** REPORT DASHBOARD */

    GetLeadsPeekInvReport(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/leadspeek/report/invoice/" +
              data.companyID +
              "/" +
              data.clientID +
              "/" +
              data.startDate +
              "/" +
              data.endDate
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    GetLeadsPeekLeadReport(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/leadspeek/report/lead/" +
              data.companyID +
              "/" +
              data.clientID +
              "/" +
              data.startDate +
              "/" +
              data.endDate,
              {
                params: {
                  Page: data.page,
                  PerPage:data.perPage,
                  sortby: data.sortby,
                  order: data.order,
                  moduleType: data.moduleType,
                  searchQuery: data.searchQuery,
                  groupCompanyID: data.groupCompanyID
                }
              }
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    GetLeadsPeekInitDate(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/leadspeek/report/initdate/" + data.companyID + "/" + data.clientID, {
              params: {
                moduleType: data.moduleType,
                groupCompanyID: data.groupCompanyID,
              }
            }
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    GetLeadsPeekChartReport(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/leadspeek/report/chart/" +
              data.companyID +
              "/" +
              data.clientID +
              "/" +
              data.startDate +
              "/" +
              data.endDate, {
                params: {
                  moduleType: data.moduleType,
                  groupCompanyID: data.groupCompanyID,
                }
              }
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /** REPORT DASHBOARD */

    activepauseLeadsPeek(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/leadspeek/client/activepause", {
            companyID: data.companyID,
            leadspeekID: data.leadspeekID,
            status: data.status,
            activeuser: data.activeuser,
            userID: data.userID,
            ip_user: data.ip_user,
            timezone: data.timezone,
            idSys: data.idSys,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    RemoveLeadsPeekClient(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/leadspeek/client/remove", {
            companyID: data.companyID,
            leadspeekID: data.leadspeekID,
            status: data.status,
            userID: data.userID
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    UpdateLeadsPeekClientLocal(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/leadspeek/client/local/update", {
            companyID: data.companyID,
            leadspeekID: data.leadspeekID,
            //locatorzip: data.locatorzip,
            //locatordesc: data.locatordesc,
            //locatorkeyword: data.locatorkeyword,
            //locatorstate: data.locatorstate,
            locatorrequire: data.locatorrequire,
            //locatorcity: data.locatorcity,
            //sificampaign: data.sificampaign,
            //sifiorganizationid: data.sifiorganizationid,
            phoneenabled: data.phoneenabled,
            homeaddressenabled: data.homeaddressenabled,
            requireemailaddress: data.requireemailaddress,
            reidentificationtype: data.reidentificationtype
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    UpdateLeadsPeekClientLocator(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/leadspeek/client/locator/update", {
            companyID: data.companyID,
            leadspeekID: data.leadspeekID,
            locatorzip: data.locatorzip,
            locatordesc: data.locatordesc,
            locatorkeyword: data.locatorkeyword,
            locatorkeywordcontextual: data.locatorkeywordcontextual,
            locatorstate: data.locatorstate,
            locatorrequire: data.locatorrequire,
            locatorcity: data.locatorcity,
            sificampaign: data.sificampaign,
            sifiorganizationid: data.sifiorganizationid,
            startdatecampaign: data.startdatecampaign,
            enddatecampaign: data.enddatecampaign,
            nationaltargeting: data.nationaltargeting,
            locationtarget: data.locationtarget,
            phoneenabled: data.phoneenabled,
            homeaddressenabled: data.homeaddressenabled,
            requireemailaddress: data.requireemailaddress,
            reidentificationtype: data.reidentificationtype
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    UpdateLeadsPeekClient(context, data) {
      return new Promise((resolve, reject) => {
        var _fieldupdate = "";
        var _valuefield = "";
        var _leadspeekType = "";

        if (
          data.leadspeekType != "" &&
          typeof data.leadspeekType != "undefined"
        ) {
          _leadspeekType = data.leadspeekType;
        }

        if (data.fieldupdate != "" && typeof data.fieldupdate != "undefined") {
          _fieldupdate = data.fieldupdate;
          _valuefield = data.valuefield;
        }
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/leadspeek/client/update", {
            companyID: data.companyID,
            userID: data.userID,
            leadspeekID: data.leadspeekID,
            reportType: data.reportType,
            reportSentTo: data.reportSentTo,
            adminNotifyTo: data.adminNotifyTo,
            leadsAmountNotification: data.leadsAmountNotification,
            fieldupdate: _fieldupdate,
            valuefield: _valuefield,
            leadspeekType: _leadspeekType,
            companyGroupID: data.companyGroupID,
            clientOrganizationID: data.clientOrganizationID,
            clientCampaignID: data.clientCampaignID,
            clientHidePhone: data.clientHidePhone,
            campaignName: data.campaignName,
            urlCode: data.urlCode,
            urlCodeThankyou: data.urlCodeThankyou,
            gtminstalled: data.gtminstalled,

            locatorzip: data.locatorzip,
            locatordesc: data.locatordesc,
            locatorkeyword: data.locatorkeyword,
            locatorkeywordcontextual: data.locatorkeywordcontextual,
            locatorstate: data.locatorstate,
            locatorrequire: data.locatorrequire,
            locatorcity: data.locatorcity,
            externalcampaign: data.externalcampaign,
            externalorganizationid: data.externalorganizationid,
            startdatecampaign: data.startdatecampaign,
            enddatecampaign: data.enddatecampaign,
            oristartdatecampaign: data.oristartdatecampaign,
            orienddatecampaign: data.orienddatecampaign,
            nationaltargeting: data.nationaltargeting,
            locationtarget: data.locationtarget,
            phoneenabled: data.phoneenabled,
            homeaddressenabled: data.homeaddressenabled,
            requireemailaddress: data.requireemailaddress,
            reidentificationtype: data.reidentificationtype,
            applyreidentificationall: data.applyreidentificationall,
            timezone: data.timezone,
            listAdvanceInformationId: data.listAdvanceInformationId,
            listB2BInformationId: data.listB2BInformationId,
            campaign_information_type: data.campaign_information_type,
            campaign_information_type_local: data.campaignInformationTypeLocal,
            adminAddType: data.adminAddType,
            adminAddId: data.adminAddId,
            answers: data.answers,
            globalviewmode: data.globalviewmode
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    purgeSuppressionList(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
        .delete(
            "/leadspeek/suppressionlist/purge/" +
              data.paramID +
              "/" +
              data.campaignType
          )
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    ResendGoogleLink(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/leadspeek/client/resendgooglelink", {
            companyID: data.companyID,
            userID: data.userID,
            leadspeekID: data.leadspeekID,
            reportSentTo: data.reportSentTo,
            adminNotifyTo: data.adminNotifyTo
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    CreateLeadsPeekClient(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/leadspeek/client/create", {
            companyID: data.companyID,
            userID: data.userID,
            companyName: data.companyName,
            reportType: data.reportType,
            reportSentTo: data.reportSentTo,
            adminNotifyTo: data.adminNotifyTo,
            leadsAmountNotification: data.leadsAmountNotification,
            enableMinimumLimitLeads: data.enableMinimumLimitLeads,
            minimumLimitLeads: data.minimumLimitLeads,
            leadspeekType: data.leadspeekType,
            companyGroupID: data.companyGroupID,
            clientOrganizationID: data.clientOrganizationID,
            clientCampaignID: data.clientCampaignID,
            clientHidePhone: data.clientHidePhone,
            campaignName: data.campaignName,
            urlCode: data.urlCode,
            urlCodeThankyou: data.urlCodeThankyou,
            answers: data.answers,
            startdatecampaign: data.startdatecampaign,
            enddatecampaign: data.enddatecampaign,
            oristartdatecampaign: data.oristartdatecampaign,
            orienddatecampaign: data.orienddatecampaign,
            gtminstalled: data.gtminstalled,
            phoneenabled: data.phoneenabled,
            homeaddressenabled: data.homeaddressenabled,
            requireemailaddress: data.requireemailaddress,
            reidentificationtype: data.reidentificationtype,
            applyreidentificationall: data.applyreidentificationall,
            locationtarget: data.locationtarget,
            timezone: data.timezone,
            idSys: data.idSys,
            listAdvanceInformationId: data.listAdvanceInformationId,
            listB2BInformationId: data.listB2BInformationId,
            campaign_information_type: data.campaign_information_type,
            campaign_information_type_local: data.campaignInformationTypeLocal,
            globalviewmode: data.globalviewmode,
            embeddedCustomParams: data.embeddedCustomParams
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    UpdateEmbeddedCustomParams(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/leadspeek/client/local/embedded-custom-params", {
            leadspeekID: data.leadspeekID,
            embeddedCustomParams: data.embeddedCustomParams,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    GetLeadsPeekClientList(context, data) {
      return new Promise((resolve, reject) => {
        var _clientID = "";
        var _leadspeekType = "";
        var _groupCompanyID = "all/";
        var _sortby = "";
        var _order = "";
        var _searchkey = "";

        if (
          data.groupCompanyID != "" &&
          typeof data.groupCompanyID != "undefined"
        ) {
          _groupCompanyID = data.groupCompanyID + "/";
        }

        if (data.clientID != "" && typeof data.clientID != "undefined") {
          _clientID = "/" + data.clientID;
        }

        if (
          data.leadspeekType != "" &&
          typeof data.leadspeekType != "undefined"
        ) {
          _leadspeekType = _groupCompanyID + data.leadspeekType + "/";
        }

        if (typeof data.sortby != "undefined" && data.sortby != "") {
          if (_clientID == "") {
            _sortby = "/0/" + data.sortby;
          } else {
            _sortby = "/" + data.sortby;
          }
        }

        if (typeof data.order != "undefined" && data.order != "") {
          if (_sortby != "") {
            _order = "/" + data.order;
          }
        }

        if (typeof data.searchkey != "undefined" && data.searchkey != "") {
          if (_sortby == "" && _clientID == "") {
            _searchkey = "/0/0/0/" + data.searchkey;
          } else {
            _searchkey = "/" + data.searchkey;
          }
        }
      let param =   {
         
          PerPage: data.perPage,
          view: data.view,
          CampaignStatus: data.campaignStatus
        }
        if(data.page){
          param.Page = data.page
        }
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/leadspeek/client/" +
              _leadspeekType +
              data.companyID +
              _clientID +
              _sortby +
              _order +
              _searchkey,
            {
              params:param
            }
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },

    disconectGoogleSheet(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/leadspeek/googlespreadsheet/revoke/" + data.companyID)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    savePlanPackage(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/configuration/setting/package", {
            CompanyID: data.CompanyID,
            packageID: data.packageID
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getRootRevenue(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(`/configuration/root-revenue/${data.startDate}/${data.endDate}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getReportAnalytics(context, data) {
      return new Promise((resolve, reject) => {
        var _companyid = "";
        var _campaignid = "";

        if (data.companyid != "" && typeof data.companyid != "undefined") {
          _companyid = "/" + data.companyid;
        }

        if (
          data.campaignid != "" &&
          typeof data.campaignid != "undefined" &&
          _companyid != ""
        ) {
          _campaignid = "/" + data.campaignid;
        }

        if (_companyid == "" && _campaignid == "" && data.companyrootid != "") {
          _companyid = "/0/0/" + data.companyrootid;
        }

        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/configuration/report-analytics/" +
              data.startDate +
              "/" +
              data.endDate +
              _companyid +
              _campaignid
          )
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getPixelLeadRecord(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        
        let url = `/configuration/pixel-lead-record/${data.startDate}/${data.endDate}`;
        if (data.companyrootid && data.companyrootid !== "") {
          url += `/${data.companyrootid}`;
        }

        axios.get(`${url}`)
             .then(response => {
              resolve(response.data);
             })
             .catch(error => {
              reject(error);
             })
      });
    },
    setAgencyExcludeMinimumSpend(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/configuration/setting/exclude-include-mininum-spend", {
            CompanyID: data.CompanyID,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },

    setAgencyOnboardingCharge(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put(`/configuration/onboarding/set`, {
            CompanyID: data.CompanyID,
            amount: data.amount,
            enable_coupon: data.enable_coupon,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getOnboardAgencySettings(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(`/configuration/onboarding/get`, {
            params: {
              companyID: data.companyID,
              companyRootID: data.companyRootID,
              settingname: data.settingname  
            }
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    createConnectedAccountLink(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/general/get-connected-account-link", {
            connectid: data.connectid,
            refreshurl: data.refreshurl,
            returnurl: data.returnurl,
            idsys: data.idsys
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },

    createExistingConnectedAccountLink(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/general/get-existing-connected-account-link", {
            company_root_id: data.company_root_id,
            company_id: data.company_id,
            email: data.email,
            user_id: data.user_id,
            user_type: data.user_type,
            subdomain: data.subdomain,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },

    createConnectedAccount(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/general/create-connected-account", {
            companyID: data.companyID,
            companyname: data.companyname,
            companyphone: data.companyphone,
            companyaddress: data.companyaddress,
            companycity: data.companycity,
            companystate: data.companystate,
            companyzip: data.companyzip,
            companycountry: data.companycountry,
            companyemail: data.companyemail,
            weburl: data.weburl,
            idsys: data.idsys
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            //console.log(error);
            reject(error.response.data.message);
          });
      });
    },
    createSalesConnectedAccount(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/general/create-sales-connected-account", {
            userID: data.usrID,
            idsys: data.idsys
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            //console.log(error);
            reject(error.response.data.message);
          });
      });
    },
    checkConnectedAccount(context, data) {
      let param = data.companyID && data.idsys ?  data.companyID + "/" + data.idsys : data.companyID;
      // console.log(param); 
      // exit;
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/general/check-connected-account/" + param
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    checkSalesConnectedAccount(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/general/check-sales-connected-account/" +
              data.usrID +
              "/" +
              data.idsys
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    checkGoogleConnectSheet(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/leadspeek/googlespreadsheet/check-connect/" + data.companyID)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    resetpaymentconnection(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .delete(
            "/general/reset-payment-connection/" +
              data.companyID +
              "/" +
              data.typeConnection
          )
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /** LEADSPEEK */

    /** CONFIGURATION APP */
    updateCustomDomain(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/configuration/update-custom-domain", {
            companyID: data.companyID,
            DownlineDomain: data.DownlineDomain,
            whitelabelling: data.whitelabelling
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getCampaignResource(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/leadspeek/getcampaignresource/" +
              data.organizationID +
              "/" +
              data.campaignID
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getDomainorSubInfo(context, data) {
      return new Promise((resolve, reject) => {
        var hostclientID = "";
        if (typeof data.hostID != "undefined" && data.hostID != "") {
          hostclientID = "/" + data.hostID;
        }
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/domainsubdomain/" + data.domainorsub + hostclientID, {
            params: {
              agency_company_id: data.agencyCompanyId || null,
            },
          })
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    }, 
    getUserCampaigns(context, data) {
      return new Promise((resolve, reject) => {        
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post(
            "/configuration/user/show-campaigns"
          , { 
              companyID: data.companyID,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getUsers(context, data) {
      return new Promise((resolve, reject) => {        
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post(
            "/configuration/user/show-user"
          , { 
              companyID: data.companyID,
              user_type: data.userType,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getUserLogs(context, data) {
      return new Promise((resolve, reject) => {        
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post(
            "/configuration/user/log-user"
          , { 
              companyID: data.companyID,
              action: data.action,
              user_type: data.userType,
              user_id: data.userID,
              page: data.page,
              leadspeek_api_id: data.campaignID,
              log_date_range: data.logDateRange,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getUserLogsCategory(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post(
            "/configuration/user/log-category"
          ,)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getGeneralSetting(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/configuration/setting/" + data.companyID + "/" + data.settingname
          , {
            params: {
              idSys: data.idSys,
              pk: data.pk,
            }
          })
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getCityStateList(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/general/state/" + data.citystate + "/cities")
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getStateList(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/general/state")
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getSalesList(context, data) {
      return new Promise((resolve, reject) => {
        var _companyID = "";

        if (data.companyID != "") {
          _companyID = "/" + data.companyID;
        }

        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/configuration/sales/list" + _companyID)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    getRootList(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/configuration/root/list")
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    setSalesPerson(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/configuration/sales", {
            companyID: data.companyID,
            salesRep: data.salesRep,
            salesAE: data.salesAE,
            salesRef: data.salesRef
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    ClientModuleCost(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/configuration/user/costmodule", {
            companyID: data.companyID,
            ClientID: data.ClientID,
            ModuleName: data.ModuleName,
            CostSet: data.CostSet,
            CostSetAdvanced: data.CostSetAdvanced,
            CostSetB2b: data.CostSetB2b,
            CostMonth: data.CostMonth,
            CostMaxLead: data.CostMaxLead,
            LimitLead: data.LimitLead,
            enableMinimumLimitLeads: data.enableMinimumLimitLeads,
            minimumLimitLeads: data.minimumLimitLeads,
            LimitLeadFreq: data.LimitLeadFreq,
            LimitLeadStart: data.LimitLeadStart,
            LimitLeadEnd: data.LimitLeadEnd,
            PaymentTerm: data.PaymentTerm,
            contiDurationSelection: data.contiDurationSelection,
            topupoptions: data.topupoptions,
            leadsbuy: data.leadsbuy,
            PlatformFee: data.PlatformFee,
            LeadspeekApiId: data.LeadspeekApiId,
            idUser: data.idUser,
            ipAddress: data.ipAddress,
            idSys: data.idSys
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    RemoveRoleModule(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .delete(
            "/configuration/role-module/remove/" +
              data.companyID +
              "/" +
              data.roleID
          )
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    addUpdateRole(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/configuration/role-module/addupdate", {
            companyID: data.companyID,
            roleID: data.roleID,
            roleName: data.roleName,
            roleIcon: data.roleIcon,
            roledata: data.roledata
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    GetRoleList(context, data) {
      return new Promise((resolve, reject) => {
        var _companyID = "";
        var _getType = "";
        var _roleID = "";
        var _usrID = "";

        if (data.getType != "") {
          _getType = data.getType;
        }
        if (data.companyID != "" && data.getType != "") {
          _companyID = "/" + data.companyID;
        }
        if (data.companyID != "" && data.getType != "" && data.roleID != "") {
          _roleID = "/" + data.roleID;
        }

        if (
          data.companyID != "" &&
          data.getType != "" &&
          data.roleID != "" &&
          data.usrID != ""
        ) {
          _usrID = "/" + data.usrID;
        }

        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/configuration/role-module/" +
              _getType +
              _companyID +
              _roleID +
              _usrID
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    RemoveClient(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .delete(
            "/configuration/user/remove/" + data.companyID + "/" + data.userID,
            {
              params: {
                params: data.params,
              }
            }
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    UpdateClient(context, data) {
      var _ClientPass = "";
      if (data.ClientPass != "") {
        _ClientPass = data.ClientPass;
      }
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/configuration/user/update", {
            companyID: data.companyID,
            ClientID: data.ClientID,
            ClientCompanyName: data.ClientCompanyName,
            ClientFullName: data.ClientFullName,
            ClientEmail: data.ClientEmail,
            ClientPhone: data.ClientPhone,
            ClientPhoneCountryCode: data.ClientPhoneCountryCode,
            ClientPhoneCountryCallingCode: data.ClientPhoneCountryCallingCode,
            ClientPass: _ClientPass,
            ClientRole: data.ClientRole,
            ClientDomain: data.ClientDomain,
            ClientWhiteLabeling: data.ClientWhiteLabeling,
            ApiMode: data.ApiMode,
            DownlineDomain: data.DownlineDomain,
            DownlineSubDomain: data.DownlineSubDomain,
            DownlineOrganizationID: data.DownlineOrganizationID,
            defaultAdmin: data.defaultAdmin,
            customercare: data.customercare,
            adminGetNotification: data.adminGetNotification,
            isAgencyOwner: data.isAgencyOwner,
            action: data.action,
            disabledreceivedemail: data.disabledreceivedemail,
            enabledDeletedAccountClient: data.enabledDeletedAccountClient,
            disabledaddcampaign: data.disabledaddcampaign,
            selectedterms: data.selectedterms,
            selectedmodules: data.selectedmodules,
            idsys: data.idsys,
            permission_active: data.permission_active,
            user_permissions: data.user_permissions,
            enablephonenumber: data.enablephonenumber,
            editorspreadsheet: data.editorspreadsheet,
            planMinSpendId: data.planMinSpendId,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error.response);
          });
      });
    },
    manualBillSettlement(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put(
            "/general/manual-bill-settlement", {
              userID: data.userID,
          })
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    testsmtp(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/configuration/user/testsmtp", {
            companyID: data.companyID,
            emailsent: data.emailsent
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    ResendInvitation(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/configuration/user/resendinvitation", {
            ClientID: data.ClientID
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    CreateClient(context, data) {
      var _ClientPass = "";
      if (data.ClientPass != "") {
        _ClientPass = data.ClientPass;
      }
      let _selectedModules = '';
      if (data.selectedModulesCreate) {
        _selectedModules = data.selectedModulesCreate
      }else if (data.selectedmodules) {
        _selectedModules = data.selectedmodules
      }
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/configuration/user/create", {
            companyID: data.companyID,
            userType: data.userType,
            ClientCompanyName: data.ClientCompanyName,
            ClientFullName: data.ClientFullName,
            ClientEmail: data.ClientEmail,
            ClientPhone: data.ClientPhone,
            ClientPhoneCountryCode: data.ClientPhoneCountryCode,
            ClientPhoneCountryCallingCode: data.ClientPhoneCountryCallingCode,
            ClientPass: _ClientPass,
            ClientRole: data.ClientRole,
            ClientDomain: data.ClientDomain,
            ClientWhiteLabeling: data.ClientWhiteLabeling,
            ApiMode: data.ApiMode,
            DownlineDomain: data.DownlineDomain,
            DownlineSubDomain: data.DownlineSubDomain,
            DownlineOrganizationID: data.DownlineOrganizationID,
            defaultAdmin: data.defaultAdmin,
            customercare: data.customercare,
            adminGetNotification: data.adminGetNotification,
            isAgencyOwner: data.isAgencyOwner,
            disabledreceivedemail: data.disabledreceivedemail,
            enabledDeletedAccountClient: data.enabledDeletedAccountClient,
            disabledaddcampaign: data.disabledaddcampaign,
            idsys: data.idsys,
            salesRep: data.salesRep,
            salesAE: data.salesAE,
            salesRef: data.salesRef,
            selectedterms: data.selectedterms,
            selectedmodules: _selectedModules,
            twoFactorAuth: data.twoFactorAuth,
            permission_active: data.permission_active,
            user_permissions: data.user_permissions,
            TokenId: data.TokenId || '',
            Amount: data.Amount || '',
            enablephonenumber: data.enablephonenumber,
            editorspreadsheet: data.editorspreadsheet,
            adminAddToAllCampaign: data.adminAddToAllCampaign,
            location_id: data.location_id, // hanya untuk client
            custom_menu_name: data.custom_menu_name, // hanya untuk client
            ip_login: data.ip_login,
            planMinSpendId: data.planMinSpendId,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    GetSalesDownlineList(context, data) {
      var _searchkey = "";
      var _sortby = "";
      var _order = "";

      if (typeof data.sortby != "undefined" && data.sortby != "") {
        _sortby = "/" + data.sortby;
      }

      if (typeof data.order != "undefined" && data.order != "") {
        if (_sortby != "") {
          _order = "/" + data.order;
        }
      }

      if (typeof data.searchkey != "undefined" && data.searchkey != "") {
        if (_sortby == "") {
          _searchkey = "/0/0/" + data.searchkey;
        } else {
          _searchkey = "/" + data.searchkey;
        }
      }

      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/configuration/sales-downline/" +
              data.companyID +
              "/" +
              data.usrID +
              "/" +
              data.idsys +
              _sortby +
              _order +
              _searchkey,
            {
              params: {
                Page: data.page,
                PerPage: data.perPage
              }
            }
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    GetClientList(context, data) {
      return new Promise((resolve, reject) => {
        var _companyID = "";
        var _idSys = "";
        var _userType = "";
        var _groupCompanyID = "/all";
        var _sortby = "";
        var _order = "";

        var _searchkey = "";

        if (
          data.groupCompanyID != "" &&
          typeof data.groupCompanyID != "undefined"
        ) {
          _groupCompanyID = "/" + data.groupCompanyID;
        }

        if (data.companyID != "") {
          _companyID = data.companyID;
        }
        if (data.idsys != "") {
          _idSys = "/" + data.idsys;
        }

        if (data.companyID != "" && data.userType != "") {
          _userType = "/" + data.userType;
        }

        if (
          data.userType != "" &&
          data.userModule != "" &&
          typeof data.userModule != "undefined"
        ) {
          _userType = _userType + "/" + data.userModule;
        }

        if (typeof data.sortby != "undefined" && data.sortby != "") {
          _sortby = "/" + data.sortby;
        }

        if (typeof data.order != "undefined" && data.order != "") {
          if (_sortby != "") {
            _order = "/" + data.order;
          }
        }

        if (typeof data.searchkey != "undefined" && data.searchkey != "") {
          if (_sortby == "") {
            _searchkey = "/0/0/" + data.searchkey;
          } else {
            _searchkey = "/" + data.searchkey;
          }
        }

        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/configuration/user/" +
              _companyID +
              _groupCompanyID +
              _idSys +
              _userType +
              _sortby +
              _order +
              _searchkey,
            {
              params: {
                Page: data.page,
                PerPage: data.perPage,
                CardStatus: data.cardStatus,
                CampaignStatus: data.campaignStatus,
                Filters: data.filters,
                Exclude: data.exclude,
                inAgencyList: data.inAgencyList
              }
            }
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    GetUnderPerformCampaigns(context, data){
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get("/configuration/get-under-perform-campaigns", {
            params: {
              campaign_ids: data.campaignIds,
              selected_date: data.selectedDate,
            }
          })
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });

    },
    GetAgencyList(context, data) {
      return new Promise((resolve, reject) => {
        var _companyID = "";
        var _userType = "";
        var _groupCompanyID = "/all";
        var _sortby = "";
        var _order = "";
        var _searchkey = "";

        if (
          data.groupCompanyID != "" &&
          typeof data.groupCompanyID != "undefined"
        ) {
          _groupCompanyID = "/" + data.groupCompanyID;
        }

        if (data.companyID != "" && data.userType != "") {
          _userType = "/" + data.userType;
        }

        if (typeof data.sortby != "undefined" && data.sortby != "") {
          _sortby = "/" + data.sortby;
        }

        if (typeof data.order != "undefined" && data.order != "") {
          if (_sortby != "") {
            _order = "/" + data.order;
          }
        }

        if (typeof data.searchkey != "undefined" && data.searchkey != "") {
          if (_sortby == "") {
            _searchkey = "/created_at/descending/" + data.searchkey;
          } else {
            _searchkey = "/" + data.searchkey;
          }
        }

        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            "/configuration/agencies-report" +
              _groupCompanyID +
              _sortby +
              _order +
              _searchkey,
            {
              params: {
                Page: data.page,
                PerPage: data.perPage,
                CardStatus: data.cardStatus,
                CampaignStatus: data.campaignStatus,
                SelectedDate: data.selectedDate,
                OpenApiStatus: data.openApiStatus,
              }
            }
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },

    testEmail(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/configuration/setting/test-email", {
            fromAddress: data.fromAddress,
            fromName: data.fromName,
            fromReplyto: data.fromReplyto,
            subject: data.subject,
            content: data.content,
            testEmailAddress: data.testEmailAddress,
            companyID: data.companyID,
            companyParentID: data.companyParentID,
            userType: data.userType
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },

    updateGeneralSetting(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/configuration/setting", {
            companyID: data.companyID,
            actionType: data.actionType,
            sidebarcolor: data.sidebarcolor,
            templatecolor: data.templatecolor,
            boxcolor: data.boxcolor,
            textcolor: data.textcolor,
            linkcolor: data.linkcolor,
            fonttheme: data.fonttheme,
            paymenttermDefault: data.paymenttermDefault,
            comsetname: data.comsetname,
            comsetval: data.comsetval
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },

    retryDomainSSL(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/configuration/retrydomainssl", {
            companyID: data.companyID,
            domain: data.domain
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log('Domain SSL retry error:', error);
            reject(error);
          });
      });
    },

    updateDefaultSubdomain(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .put("/configuration/subdomain", {
            companyID: data.companyID,
            subdomain: data.subdomain,
            idsys: data.idsys
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /** CONFIGURATION APP */

    /** MARKETING SIMPLI.FI */
    DeleteOrganization(context, data) {
      return new Promise((resolve, reject) => {
        axios
          .delete("/marketing/deleteorganization/" + data.OrganizationID)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    GetOrganizationList(context, data) {
      return new Promise((resolve, reject) => {
        var _organizationID = "";
        var _requestType = "";

        if (data.OrganizationID != "") {
          _organizationID = data.OrganizationID;
        }
        if (data.OrganizationID != "" && data.RequestType != "") {
          _requestType = "/" + data.RequestType;
        }

        axios
          .get("/marketing/getorganization/" + _organizationID + _requestType)
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data.organizations);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    CreateOrganization(context, data) {
      return new Promise((resolve, reject) => {
        axios
          .post("/marketing/createorganization", {
            OrganizationName: data.OrganizationName,
            ParentID: data.ParentID,
            CustomID: data.CustomID
          })
          .then(response => {
            resolve(response.data.organizations);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    /** MARKETING SIMPLI.FI */
    GetBannerList(context, data) {
      return new Promise((resolve, reject) => {
        axios
          .post("/banner/show", {
            bannertype: data.bannertype,
            //bannersethash: data.bannersethash,
            username: this.state.userData.email,
            type: data.type
          })
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error.response.data.errors);
            //resolve('failed');
          });
      });
    },
    StartBannerCreate(context, data) {
      return new Promise((resolve, reject) => {
        axios
          .post("/banner/startbannercreate", {
            bannertype: data.bannertype,
            banneranimated: data.banneranimated,
            username: this.state.userData.email,
            name: this.state.userData.name
          })
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error.response.data.errors);
            //resolve('failed');
          });
      });
    },
    forgetPass(context, data) {
      return new Promise((resolve, reject) => {
        axios
          .post("/forgetpass", {
            username: data.username,
            gtoken: data.gtoken,
            ownedcompanyid: data.ownedcompanyid,
            companyrootid: data.companyrootid
          })
          .then(response => {
            resolve("success");
          })
          .catch(error => {
             let message = 'Something went wrong. Please try again later.';
  
              if (
                error &&
                error.response &&
                error.response.data &&
                error.response.data.message
              ) {
                message = error.response.data.message;
              }

            reject({ message: message });
            //resolve('failed');
          });
      });
    },
    requestOptOut(context, data) {
      return new Promise((resolve, reject) => {
        axios
          .post("/opt-out", {
            full_name: data.full_name,
            email: data.email,
            phone_number: data.phone_number,
            address: data.address,
            ownedcompanyid: data.ownedcompanyid,
            companyrootid: data.companyrootid,
            domainName: data.domainName,
            gtoken: data.gtoken
          })
          .then(response => {
            // Expect backend to return { title, message, ... } in response.data
            resolve(response);
          })
          .catch(error => {
            let message = "Unable to submit your request right now. Please try again later.";

            if (
              error &&
              error.response &&
              error.response.data &&
              error.response.data.message
            ) {
              message = error.response.data.message;
            }

            reject({ message: message, raw: error });
          });
      });
    },
    register(context, data) {
      return new Promise((resolve, reject) => {
        var _companyname = "";
        var _phonenum = "";
        var _phone_country_code = "";
        var _phone_country_calling_code = "";
        var _userType = "client";
        var _refcode = "";

        if (data.companyname != "") {
          _companyname = data.companyname;
        }

        if (data.phonenum != "") {
          _phonenum = data.phonenum;
        }

        if (data._phone_country_code != "") {
          _phone_country_code = data.phone_country_code;
        }

        if (data._phone_country_calling_code != "") {
          _phone_country_calling_code = data.phone_country_calling_code;
        }

        if (data.userType != "") {
          _userType = data.userType;
        }

        if (data.refcode != "") {
          _refcode = data.refcode;
        }

        axios
          .post("/register", {
            companyname: _companyname,
            name: data.name,
            email: data.email,
            phonenum: _phonenum,
            phone_country_code : _phone_country_code,
            phone_country_calling_code : _phone_country_calling_code,
            password: data.password,
            gtoken: data.gtoken,
            userType: data.userType,
            domainName: data.domainName,
            ownedcompanyid: data.ownedcompanyid,
            idsys: data.idsys,
            refcode: _refcode,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            /*console.log(error.response.data.errors.email);
            console.log('errorType', typeof error);
            console.log('error', Object.assign({}, error));
            console.log('getOwnPropertyNames', Object.getOwnPropertyNames(error));
            console.log('stackProperty', Object.getOwnPropertyDescriptor(error, 'stack'));
            console.log('messageProperty', Object.getOwnPropertyDescriptor(error, 'message'));
            console.log('stackEnumerable', error.propertyIsEnumerable('stack'));
            console.log('messageEnumerable', error.propertyIsEnumerable('message'));
            */
            //console.log(error.response.data.errors.email[0]);
            //console.log(error.response.data.errors.captcha[0]);

            /*Object.keys(error.response.data.errors).forEach(key => {
            console.log(error.response.data.errors[key][0]);
           })*/
            //console.log(error.response.data.message);
            reject(error.response.data.errors);
            //resolve('failed');
          });
      });
    },
    destroyToken(context) {
      stopAutoRefresh();
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;

      if (context.getters.loggedIn) {
        return new Promise((resolve, reject) => {
          axios
            .post("/logout")
            .then(response => {
              localStorage.removeItem("access_token");
              localStorage.removeItem("refresh_token");
              localStorage.removeItem("expires_in");
              localStorage.removeItem("token_expires_at");
              localStorage.removeItem("userData");
              localStorage.removeItem("userDataOri");
              localStorage.removeItem("userRole");
              localStorage.removeItem("clientIP");
              sessionStorage.removeItem("userData");
              sessionStorage.removeItem("userDataOri");
              sessionStorage.removeItem("userDataAgency");
              sessionStorage.removeItem(VIEW_AS_ACTIVE_KEY);
              context.commit("destroyToken");
              resolve(response);
            })
            .catch(error => {
              localStorage.removeItem("access_token");
              localStorage.removeItem("refresh_token");
              localStorage.removeItem("expires_in");
              localStorage.removeItem("token_expires_at");
              localStorage.removeItem("userData");
              localStorage.removeItem("userDataOri");
              localStorage.removeItem("userRole");
              localStorage.removeItem("clientIP");
              sessionStorage.removeItem("userData");
              sessionStorage.removeItem("userDataOri");
              sessionStorage.removeItem("userDataAgency");
              sessionStorage.removeItem(VIEW_AS_ACTIVE_KEY);
              context.commit("destroyToken");
              reject(error);
            });
        });
      }
    },

    putTokenSocial(context, credentials) {
      return new Promise((resolve, reject) => {
        const token = credentials.acctoken;

        localStorage.setItem("access_token", token);
        localStorage.setItem("token_expires_at", String(Date.now() + (parseInt(localStorage.getItem('expires_in') || '7200') * 1000)));
        context.commit("retrieveToken", token);
        setupAutoRefresh();
        resolve("success");
      });
    },

    storeRegistrationToken(context, tokenData) {
      return new Promise((resolve) => {
        const token = tokenData.access_token;
        const expiresIn = tokenData.expires_in || 7200;

        localStorage.setItem("access_token", token);
        localStorage.setItem("expires_in", expiresIn);
        localStorage.setItem("token_expires_at", String(Date.now() + (expiresIn * 1000)));

        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        context.commit("retrieveToken", token);
        setupAutoRefresh();
        resolve("success");
      });
    },

    retrieveToken(context, credentials) {
      return new Promise((resolve, reject) => {
        axios
          .post("/login", {
            username: credentials.username,
            password: credentials.password,
            gtoken: credentials.gtoken,
            domainName: credentials.domainName,
            ownedcompanyid: credentials.ownedcompanyid,
            companyrootid: credentials.companyrootid
          })
          .then(response => {
            if (response.data.access_token) {
              const token = response.data.access_token;
              const expiresIn = response.data.expires_in;

              localStorage.setItem("access_token", token);
              localStorage.setItem("expires_in", expiresIn);
              localStorage.setItem("token_expires_at", String(Date.now() + (expiresIn * 1000)));
              // refresh_token is now delivered via HttpOnly cookie (set by backend)

              axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
              context.commit("retrieveToken", token);
              setupAutoRefresh();
            }
            resolve(response);
          })
          .catch(error => {
              //console.log(error.response.data)
            reject(error);
          });
      });
    },

    ssoValidationToken(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + data.token;
        axios.post('/v1/developer/sso/validation', {
          subdomain: data.subdomain,
          currentUserID: data.currentUserID
        })
        .then(response => {
          const token = response.data.access_token;

          if(response.data.action == 'replace_user') {
            localStorage.removeItem("userData");
            localStorage.removeItem("userDataOri");
            localStorage.removeItem("userRole");
            localStorage.removeItem("clientIP");
            context.commit("destroyToken");
          }

          var expiresIn = response.data.expires_in || parseInt(localStorage.getItem('expires_in') || '7200');
          localStorage.setItem("access_token", token);
          localStorage.setItem("expires_in", expiresIn);
          localStorage.setItem("token_expires_at", String(Date.now() + (expiresIn * 1000)));
          context.commit("retrieveToken", token);
          setupAutoRefresh();
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
      });
    },

    retrieveAutomComplete(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get("/" + data.category + "/autocomplete/" + data.searchtext)
          .then(response => {
            context.commit("putResultData", response.data);
            resolve("success");
          })
          .catch(error => {
            resolve("failed");
          });
      });
    },

    checkUserSetupComplete(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get("/user/checksetupcomplete/" + data.usrID)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },

    userSetupComplete(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .put("/leadspeek/user/setupcomplete", {
            usrID: data.usrID,
            statuscomplete: data.statuscomplete,
            answers: data.answers,
            companyGroupID: data.companyGroupID,
            startdatecampaign: data.startdatecampaign,
            enddatecampaign: data.enddatecampaign,
            oristartdatecampaign: data.oristartdatecampaign,
            orienddatecampaign: data.orienddatecampaign,
            phoneenabled: data.phoneenabled,
            homeaddressenabled: data.homeaddressenabled,
            requireemailaddress: data.requireemailaddress,
            phoneenabledsiteid: data.phoneenabledsiteid,
            homeaddressenabledsiteid: data.homeaddressenabledsiteid,
            requireemailaddresssiteid: data.requireemailaddresssiteid,
            timezone: data.timezone
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    switchUserData(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get("/user/" + data.usrID)
          .then(response => {
            const switchDataUser = response.data;
            const masterUser = context.getters.userData;
            // console.log({switchDataUser, masterUser});
    
            /**
             * View-as state in sessionStorage (per-tab) to avoid multi-tab overwrite.
             * kondisi 1: root view as agency, lalu view as lagi sebagai client (new)
             */
            const alreadyInViewAs = sessionStorage.getItem('userDataOri');
            if (alreadyInViewAs)
            {
              const companyGroupSelected = localStorage.getItem("companyGroupSelected");
              const subdomainAgency = localStorage.getItem("subdomainAgency");
              const rootcomp = localStorage.getItem("rootcomp") === "true";

              if (switchDataUser["user_type"] == "client") {
                localStorage.removeItem("companyGroupSelected");
                localStorage.setItem("companyGroupSelected", switchDataUser["id"]);
              } else {
                localStorage.removeItem("companyGroupSelected");
                localStorage.setItem("companyGroupSelected", "");
              }

              switchDataUser["user_type_ori"] = masterUser["user_type_ori"];
              masterUser['agencyLocalStorageBackup'] = { companyGroupSelected, subdomainAgency, rootcomp };
              const envryptedObjectOri = CryptoJS.AES.encrypt(JSON.stringify(masterUser), process.env.VUE_APP_SECRET_KEY_CRYPTOJS);

              sessionStorage.removeItem("userDataAgency");
              sessionStorage.setItem("userDataAgency", envryptedObjectOri);
              sessionStorage.setItem(VIEW_AS_ACTIVE_KEY, '1');

              switchDataUser.user_permissions_view_mode = null
              if (masterUser.user_permissions != null) {
                switchDataUser.user_permissions_view_mode = JSON.parse(masterUser.user_permissions)
              }
              if (masterUser.rootcomp) {
                switchDataUser.isViewMode = true
              }

              localStorage.removeItem('subdomainAgency');
              localStorage.removeItem('rootcomp');
            }
            /**
             * kondisi: first-level view-as (root/agency view as client, or root view as agency)
             */
            else
            {
              if (switchDataUser["user_type"] == "client") {
                localStorage.removeItem("companyGroupSelected");
                localStorage.setItem("companyGroupSelected", switchDataUser["id"]);
              } else {
                localStorage.removeItem("companyGroupSelected");
                localStorage.setItem("companyGroupSelected", "");
              }
  
              switchDataUser["user_type_ori"] = masterUser["user_type"];
              const envryptedObjectOri = CryptoJS.AES.encrypt(JSON.stringify(masterUser), process.env.VUE_APP_SECRET_KEY_CRYPTOJS);

              sessionStorage.removeItem("userDataOri");
              sessionStorage.setItem("userDataOri", envryptedObjectOri);
              sessionStorage.setItem(VIEW_AS_ACTIVE_KEY, '1');

              switchDataUser.user_permissions_view_mode = null
              if (masterUser.user_permissions != null) {
                switchDataUser.user_permissions_view_mode = JSON.parse(masterUser.user_permissions)
              }
              if (masterUser.systemuser && masterUser.rootcomp) {
                switchDataUser.isViewMode = true
              }
            }
            
            context.commit("retrieveUser", switchDataUser);
            resolve("success");
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    retrieveUserData(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        var userID = "";
        if (typeof data.usrID != "undefined" && data.usrID != "") {
          userID = "/" + data.usrID;
        }
        axios
          .get("/user" + userID)
          .then(response => {
            const dataUser = response.data;
            context.commit("retrieveUser", dataUser);
            //resolve(response)
            //console.log(response.data);
            // context.commit('addTodo', response.data)
            resolve("success");
          })
          .catch(error => {
            //localStorage.removeItem('access_token')
            //context.commit('destroyToken')
            //reject(error)
            resolve("failed");
          });
      });
    },
    setUserData(context, data) {
      context.commit("retrieveUser", data.user);
    },
    updatePass(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .post("/user/resetpassword", {
            usrID: data.usrID,
            newpassword: data.newpassword,
            currpassword: data.currpassword
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    docDownloadAuth(context, data) {
      return new Promise((resolve, reject) => {
        axios
          .post("/docauth", {
            pwd: data.password,
            urldata: data.dataurl
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    saveProfileStep1(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        let formData = new FormData();
        formData.append("name", data.name);
        formData.append("email", data.email);
        formData.append("phone", data.phone);
        formData.append("phoneCountryCode", data.phoneCountryCode);
        formData.append("phoneCountryDialCode", data.phoneCountryDialCode);
        formData.append("address", data.address);
        formData.append("city", data.city);
        formData.append("country", data.country);
        formData.append("zip", data.zip);
        formData.append("state", data.state);
        formData.append("pict", data.pict);
        formData.append("currpict", data.currpict);
        formData.append("profilestep", data.profilestep);
        formData.append("newpass", data.newpass);
        formData.append("renewpass", data.renewpass);
        formData.append("id", data.id);
        formData.append("idsys", data.idsys);

        axios
          .post("/user/update", formData)
          .then(response => {
            if (response.data.result == 'error_email') {
              resolve(response.data.message);
            } else {
              const updatedData = {};
              if (response.data.image_url != "") {
                updatedData.profile_pict = response.data.image_url;
              } else if (response.data.picexist == "") {
                updatedData.profile_pict = "";
              }
              updatedData.name = data.name;
              updatedData.email = data.email;
              updatedData.phonenum = data.phone;
              updatedData.phone_country_code = data.phoneCountryCode;
              updatedData.phone_country_calling_code = data.phoneCountryDialCode;
              updatedData.address = data.address;
              updatedData.city = data.city;
              updatedData.zip = data.zip;
              updatedData.country_code = data.country;
              updatedData.state_code = data.state;
  
              context.commit('updateUserData', updatedData)
              //console.log(response.data.image_url);
              //resolve(response)
              //console.log(response.data);
              // context.commit('addTodo', response.data)
              resolve("success");
            }
            
          })
          .catch(error => {
            //localStorage.removeItem('access_token')
            //context.commit('destroyToken')
            //reject(error)
            resolve("failed");
          });
      });
    },

    saveProfileStep2(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        let formData = new FormData();

        if (data.id != "" && data.id != "" && typeof data.id != "undefined") {
          formData.append("id", data.id);
        }

        formData.append("industryID", data.industryID);
        formData.append("industryName", data.industryName);
        formData.append("companyID", data.companyID);
        formData.append("companyName", data.companyName);
        formData.append("companyphone", data.companyphone);
        formData.append(
          "companyphoneCountryCode",
          data.companyphoneCountryCode
        );
        formData.append(
          "companyPhoneCountryCallingCode",
          data.companyPhoneCountryCallingCode
        );
        formData.append("companyemail", data.companyemail);

        formData.append("companyaddress", data.companyaddress);
        formData.append("companycity", data.companycity);
        formData.append("companyzip", data.companyzip);
        formData.append("companystate", data.companystate);
        formData.append("companycountry", data.companycountry);
        formData.append("franchize", data.franchize);
        formData.append("franchizedirector", data.franchizedirector);

        formData.append("directorfn", data.directorfn);
        formData.append("directorln", data.directorln);
        formData.append("directoremail", data.directoremail);
        formData.append("directorphone", data.directorphone);

        formData.append("officemanager", data.officemanager);
        formData.append("managerfn", data.managerfn);
        formData.append("managerln", data.managerln);
        formData.append("manageremail", data.manageremail);
        formData.append("managerphone", data.managerphone);

        formData.append("team", data.team);
        formData.append("teammanager", data.teammanager);
        formData.append("teamfn", data.teamfn);
        formData.append("teamln", data.teamln);
        formData.append("teamphone", data.teamphone);
        formData.append("teamemail", data.teamemail);

        formData.append("pict", data.pict);
        formData.append("currpict", data.currpict);
        formData.append("profilestep", data.profilestep);

        formData.append("DownlineSubDomain", data.DownlineSubDomain);
        formData.append("idsys", data.idsys);

        axios
          .post("/user/update", formData)
          .then(response => {
            const updatedData = {}
            if (response.data.image_url != "") {
              updatedData.company_logo = response.data.image_url;
            } else if (response.data.picexist == "") {
              updatedData.company_logo = "";
            }
            updatedData.industry_id = response.data.industryID;
            updatedData.company_id = response.data.companyID;

            updatedData.industry_name = data.industryName;
            updatedData.company_name = data.companyName;
            updatedData.company_phone = data.companyphone;
            updatedData.company_phone_country_code =
              data.companyphoneCountryCode;
            updatedData.company_phone_country_calling_code =
              data.companyPhoneCountryCallingCode;
            updatedData.company_email = data.companyemail;
            updatedData.company_address = data.companyaddress;

            updatedData.company_city = data.companycity;
            updatedData.company_zip = data.companyzip;
            updatedData.company_state_code = data.companystate;
            updatedData.company_country_code = data.companycountry;

            updatedData.franchize = data.franchize;
            updatedData.franchizedirector = data.franchizedirector;
            updatedData.directorfn = data.directorfn;
            updatedData.directorln = data.directorln;
            updatedData.directoremail = data.directoremail;
            updatedData.directorphone = data.directorphone;

            updatedData.officemanager = data.officemanager;
            updatedData.managerfn = data.managerfn;
            updatedData.managerln = data.managerln;
            updatedData.manageremail = data.manageremail;
            updatedData.managerphone = data.managerphone;

            updatedData.team = data.team;
            updatedData.teammanager = data.teammanager;

            updatedData.teamfn = data.teamfn;
            updatedData.teamln = data.teamln;
            updatedData.teamemail = data.teamemail;
            updatedData.teamphone = data.teamphone;
            context.commit('updateUserData', updatedData)

            resolve(response.data.result);
          })
          .catch(error => {
            //localStorage.removeItem('access_token')
            //context.commit('destroyToken')
            //reject(error)
            resolve("failed");
          });
      });
    },

    getUserData(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        var userID = "";
        if (typeof data.usrID != "undefined" && data.usrID != "") {
          userID = "/" + data.usrID;
        }
        axios
          .get("/user" + userID)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },

    getAgencyWhitelabelDomain(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        var company_id = "";
        if (typeof data.companyID != "undefined" && data.companyID != "") {
          company_id = "/" + data.companyID;
        }
        axios
          .get("/company/whitelabel-domain" + company_id)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },

    // Integrations api
    generateGHLv2AuthUrl(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/gohighlevelv2/generateauthurl', {
          company_id: data.company_id,
          subdomain_url: data.subdomain_url,
          enable_sendgrid: data.enable_sendgrid,
          custom_fields_general: data.custom_fields_general,
          custom_fields_b2b: data.custom_fields_b2b,
          custom_fields_advance: data.custom_fields_advance,
          custom_menu_name: data.custom_menu_name,
          user_ip: data.user_ip,
          is_in_iframe: data.is_in_iframe
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    generateSendJimAuthUrl(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/sendjim/generateauthurl', {
          company_id: data.company_id,
          subdomain_url: data.subdomain_url,
          user_ip: data.user_ip,
          client_id: data.client_id,
          client_secret: data.client_secret,
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    disconnectSendJim(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/sendjim/disconnect', {
          company_id: data.company_id,
          user_ip: data.user_ip,
        })
        .then(response => { resolve(response.data); })
        .catch(error => { reject(error); })
      });
    },
    disconnectGhlv2(context, data) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.post('/gohighlevelv2/disconnect', {
          company_id: data.company_id,
          user_ip: data.user_ip,
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        })
      });
    },
    async saveIntegrationSettings(
      { state },
      { url = "/saveintegration", data, silent = false }
    ) {
      try {
        const response = await axios({
          method: "post",
          url,
          headers: {
            Authorization: "Bearer " + state.token
          },
          data
        });
        if(!silent){
          if(response.data.result === 'error'){
            Vue.prototype.$notify({
              type: 'primary',
              message: response.data.message,
              icon: 'far fa-bug'
          });
          }else{
            Vue.prototype.$notify({
              type: 'success',
              message: response.data.message,
              icon: 'far fa-save'
          });
          }
        }
        return response.data;
      } catch (error) {
        if(!silent){
          Vue.prototype.$notify({
            type: "primary",
            message: error.response.data.message,
            icon: "fas fa-bug"
          });
        }
        return error.response.data
        // throw error;
      }
    },
    async updateCompanyIntegrationConfiguration(
      { state },
      { url = "/savecampaignintegrationdetails", data }
    ) {
      try {
        const response = await axios({
          method: "post",
          url,
          headers: {
            Authorization: "Bearer " + state.token
          },
          data
        });
        Vue.prototype.$notify({
          type: "success",
          message: "Changes saved successfully",
          icon: "far fa-save"
        });
        return response.data;
      } catch (error) {
        Vue.prototype.$notify({
          type: "primary",
          message: error.response.data.message,
          icon: "fas fa-bug"
        });
        // throw error;
      }
    }, 
    async getSelectedKartraListAndTags({ state }, { url = '/integration/details', data }) {
      try {
        const response = await axios({
          method: 'post',
          url,
          headers: {
            'Authorization': 'Bearer ' + state.token
          },
          data
        });    
        return response.data;
      } catch (error) {
        Vue.prototype.$notify({
          type: 'primary',
          message: error.response.data.message,
          icon: 'fas fa-bug'
      }); 
        // throw error;
      }
    }, 
    async getUserSendgridList({ state }, { url = '/sendgridlist/', companyID }) {
      try {
        const response = await axios({
          method: "get", // Changed from 'post' to 'get'
          url: url + companyID,
          headers: {
            Authorization: "Bearer " + state.token
          }
          // No data property for GET request
        });
        if (response.data) {
          return response.data.param.result;
        } else {
          return [];
        }
      } catch (error) {
        if(error.response.data.error){
          return error.response.data
        }
        return [];
        // throw error;
      }
    },  
    async getSendJimCampaignTags({ state }, { campaign_id, url = '/sendjim/getcampaigntags/' }) {
      try {
        const response = await axios({
          method: 'get',
          url: url + campaign_id,
          headers: { 'Authorization': 'Bearer ' + state.token },
        });
        if (response && response.data && response.data.result === 'success') {
          return Array.isArray(response.data.data) ? response.data.data : [];
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    async getSendJimQuickSends({ state }, { company_id, url = '/sendjim/quicksends' }) {
      try {
        const response = await axios({
          method: 'get',
          url,
          headers: { 'Authorization': 'Bearer ' + state.token },
          params: { company_id }
        });
        if (response && response.data && response.data.result === 'success') {
          const payload = response.data.data || {};
          const list = Array.isArray(payload.QuickSends) ? payload.QuickSends : [];
          // normalize to { id, name, image }
          return list.map(qs => {
            const firstSeq = Array.isArray(qs.QuickSendSequences) && qs.QuickSendSequences.length > 0 ? qs.QuickSendSequences[0] : null;
            return {
              id: qs.QuickSendID || qs.Id || qs.id,
              name: qs.Name || (firstSeq && firstSeq.Name) || 'Quick Send',
              image: (firstSeq && (firstSeq.FrontImageThumbURL || firstSeq.FrontImageURL)) || '',
              raw: qs,
            };
          }).filter(it => it.id);
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    async geUsertKartraList({ state }, { url = '/kartra/list/', companyID }) {
      try {
        const response = await axios({
          method: 'get', // Changed from 'post' to 'get'
          url: url + companyID,
          headers: {
            'Authorization': 'Bearer ' + state.token
          },
          // No data property for GET request
        });
        if(response.data){
          return response.data.param.lists
        }else{
          return []
        }
      } catch (error) {
        return [];
        // throw error;
      }
    },  
    async geUsertKartraDetails({ state }, { url = '/GetKartraListAndTag/', companyID }) {
      try {
        const response = await axios({
          method: 'get', // Changed from 'post' to 'get'
          url: url + companyID,
          headers: {
            'Authorization': 'Bearer ' + state.token
          },
          // No data property for GET request
        });
        if(response.data){
          return response.data.param
        }else{
          return {}
        }
      } catch (error) {
        if(error.response.data.error){
          return error.response.data
        }
        
        return [];
        // throw error;
      }
    },    
    async getUserIntegrationDetails({ state }, { url = '/getcompanyintegrationdetail/', companyID,slug }) {
      try {
        const response = await axios({
          method: "get",
          url: url + companyID + "/" + slug,
          headers: {
            Authorization: "Bearer " + state.token
          }
        });
        //if (response.data.data) {
        if (response.data) {
          return response.data;
        } else {
          //return {};
          return response.data;
        }
      } catch (error) {
        Vue.prototype.$notify({
          type: "primary",
          message: error.response.data.message,
          icon: "fas fa-bug"
        });
        // throw error;
      }
    },
    async getUserIntegrationList(
      { state },
      { url = "/getcompanyintegrationlist", companyID, companyParent }
    ) {
      try {
        const response = await axios({
          method: "get",
          url: `${url}/${companyID}/${companyParent}`,
          headers: {
            Authorization: "Bearer " + state.token
          }
        });
        if (response.data.data) {
          return response.data.data;
        } else {
          return [];
        }
      } catch (error) {
        Vue.prototype.$notify({
          type: "primary",
          message: error.response.data.message,
          icon: "fas fa-bug"
        });
        return [];
        // throw error;
      }
    },
    // agency zoom
    async getCampaignAgencyZoomDetails({ state }, { url = '/agency-zoom/getcampaignwebhook/', campaign_id }) {
      try {
        const response = await axios({
          method: "get",
          url: url + campaign_id,
          headers: {
            Authorization: "Bearer " + state.token
          }
        });
        if (response.data.data) {
          return response.data.data;
        } else {
          return {};
        }
      } catch (error) {
        Vue.prototype.$notify({
          type: "primary",
          message: error.response.data.message,
          icon: "fas fa-bug"
        });
        // throw error;
      }
    },
    async getClientAgencyZoometails({ state }, { url = '/agency-zoom/getclientwebhook/', company_id }) {
      try {
        const response = await axios({
          method: "get",
          url: url + company_id,
          headers: {
            Authorization: "Bearer " + state.token
          }
        });
        if (response.data.data) {
          return response.data.data;
        } else {
          return {};
        }
      } catch (error) {
        Vue.prototype.$notify({
          type: "primary",
          message: error.response.data.message,
          icon: "fas fa-bug"
        });
        // throw error;
      }
    },
    // agency zoom

    // zapier
    async getCampaignZapierDetails({ state }, { url = '/getcampaignwebhook/', campaign_id }) {
      try {
        const response = await axios({
          method: "get",
          url: url + campaign_id,
          headers: {
            Authorization: "Bearer " + state.token
          }
        });
        if (response.data.data) {
          return response.data.data;
        } else {
          return {};
        }
      } catch (error) {
        Vue.prototype.$notify({
          type: "primary",
          message: error.response.data.message,
          icon: "fas fa-bug"
        });
        // throw error;
      }
    },
    async getCampaignZapierTags({ state }, { url = '/getcampaigntags/', campaign_id }) {
      try {
        const response = await axios({
          method: "get",
          url: url + campaign_id,
          headers: {
            Authorization: "Bearer " + state.token
          }
        });
        if (response.data.data) {
          return response.data.data;
        } else {
          return {};
        }
      } catch (error) {
        Vue.prototype.$notify({
          type: "primary",
          message: error.response.data.message,
          icon: "fas fa-bug"
        });
        // throw error;
      }
    },
    async getAgencyZapierDetails({ state }, { url = '/getagencywebhook/', company_id }) {
      try {
        const response = await axios({
          method: "get",
          url: url + company_id,
          headers: {
            Authorization: "Bearer " + state.token
          }
        });
        if (response.data.data) {
          return response.data.data;
        } else {
          return {};
        }
      } catch (error) {
        Vue.prototype.$notify({
          type: "primary",
          message: error.response.data.message,
          icon: "fas fa-bug"
        });
        // throw error;
      }
    },
    async getAgencyZapierTags({ state }, { url = '/getagencytags/', company_id }) {
      try {
        const response = await axios({
          method: "get",
          url: url + company_id,
          headers: {
            Authorization: "Bearer " + state.token
          }
        });
        if (response.data.data) {
          return response.data.data;
        } else {
          return {};
        }
      } catch (error) {
        Vue.prototype.$notify({
          type: "primary",
          message: error.response.data.message,
          icon: "fas fa-bug"
        });
        // throw error;
      }
    },
    // zapier

    getSettingTwoFactorAuth(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get("/setting/twofactorauth/" + data.userId)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    settingTwoFactorAuth(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .put("/setting/twofactorauth/" + data.userId, {
            userId: data.userId,
            two_factor_auth: data.two_factor_auth,
            two_factor_auth_type: data.two_factor_auth_type,
            secretKey: data.secretKey,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    verifyLogin(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .post("/verifylogin", {
            email: data.email,
            code: data.code,
            otp_token: data.otp_token,
            ownedcompanyid: data.ownedcompanyid,
            companyrootid: data.companyrootid,
          })
          .then(response => {
            const responseData = response.data.rows ? response.data.rows : response.data;
            if (responseData.access_token) {
              const token = responseData.access_token;
              const expiresIn = responseData.expires_in;
              localStorage.setItem("access_token", token);
              localStorage.setItem("expires_in", expiresIn);
              localStorage.setItem("token_expires_at", String(Date.now() + (expiresIn * 1000)));
              // refresh_token is now delivered via HttpOnly cookie (set by backend)

              axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

              context.commit("retrieveToken", token);
              setupAutoRefresh();
           }
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    resendCode(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .post("/resendcode", {
            email: data.email,
            otp_token: data.otp_token,
            companyrootid: data.companyrootid,
            ownedcompanyid: data.ownedcompanyid,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    getStopWords(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get("/bigdbm/services/configdata/" + data.company_root_id)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getGoogleTfa(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/setting/get-google-tfa/${data.userId}/${data.companyId}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    checkCampaignActive(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .post('/leadspeek/check-campaign-active', data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getLocationBigDbm(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get('/general/enhance/location', {
            params: {
              type: data.type,
              param: data.param,
              limit: data.limit,
              search: data.search
            }
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    async userLogSaved(
      { state },
      { url = "/general/user-log-saved", data }
    ) {
      try {
        const response = await axios({
          method: "post",
          url,
          headers: {
            Authorization: "Bearer " + state.token
          },
          data
        });
        
        return response.data;
      } catch (error) {
        
        // throw error;
      }
    }, 
    userServiceAgreementConfirm(
      { state },
      { url = "/general/user-service-agreement-confirm", data }
    ) {
      try {
        const response = axios({
          method: "post",
          url,
          headers: {
            Authorization: "Bearer " + state.token
          },
          data
        });
        
        return response.data;
      } catch (error) {
        
        // throw error;
      }
    }, 
    downloadAgreementFile(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      if (!data.user_id) {
        return Promise.reject('error')
      }
      return new Promise((resolve, reject) => {
        axios
          .post(`/general/user-service-agreement-download/${data.user_id}`, data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            const data = error.response && error.response.data
              ? error.response.data
              : { message: (error.message || 'Request failed') };
            reject(data);
          });
      });
    },
    getApproxLeadCount(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/bigdbm/services/getcountcampaign/${data.id}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    onApproxLeadCount(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .post('/bigdbm/services/queuecount', data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    onApplyCampaign(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .put(`/bigdbm/services/applycampaign/${data.campaign_id}/${data.type}`, {
            keywords: data.keywords,
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getCampaignById(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/leadspeek/getcampaign/${data.campaign_id}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getCountCampaignHistory(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/bigdbm/services/getcountcampaignhistory/${data.campaign_id}/${data.type}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    runTestEmbedCode(context, data) {
      axios.defaults.headers.common["Authorization"] =
      "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/check-run-embeded-code`, {
            params: data
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    onSendTestEmbedCode(context, data) {
      axios.defaults.headers.common["Authorization"] =
      "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .post('/check-site-embeded-code', data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            if(error.response.status === 504){
              const responseError = {
                result: 'failed from crawl',
                status: 'not exist',
                message: 'Blocked from crawling (Needs manual review at your url)',
                msg_exist: 'Blocked from crawling (Needs manual review at your url)',
                msg_notexist: 'Blocked from crawling (Needs manual review at your url)',
                msg_block: 'Blocked from crawling (Needs manual review at your url)',
              }

             return reject(responseError)
            }
            
            reject(error.response.data);
          });
      });
    },
    onSendTestLead(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .post('/send-test-lead', data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            if(error.response.status == 429){
              const messageErrorToManyRequest = {
                message: 'You can only attempt this test once every minute. Please try again later.'
              }
              
              return reject(messageErrorToManyRequest)
            }


            reject(error.response.data);
          });
      });
    },
    onSendTestSpreadsheet(context, data){
      const userData = context.getters.userData;

      return new  Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.put('/leadspeek/spreadsheet/check', {
          userID: userData.id,
          campaignID: data.campaignID,
        })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    getHistorySpreadsheet(context, data){
      return new  Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.get(`/leadspeek/spreadsheet/history/${data.campaignID}`)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    onResetSpreadsheet(context, data){
      const userData = context.getters.userData;
      const userViewMode = userData.isViewMode ? userData.isViewMode : false;
      let userLogin = userData.id;
      if(userViewMode){
          const rawOri = sessionStorage.getItem('userDataOri') || localStorage.getItem('userDataOri');
          if (rawOri) {
            const userRoot = decryptData(rawOri);
            userLogin = userRoot.id;
          }
      }

      return new  Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios.put('/leadspeek/spreadsheet/reset', {
          userLogin: userLogin,
          campaignID: data.campaignID,
        })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    getMarketingServicesAgreement(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/marketing-services-agreement/${data.company_id}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    marketingServicesAgreement(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .put(`/marketing-services-agreement`, data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getMarketingServicesAgreementFeature(context, data) {
      axios.defaults.headers.common["Authorization"] =
      "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/marketing-services-agreement-feature/${data.user_id}/${data.feature_id}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    marketingServicesAgreementFeature(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .put(`/marketing-services-agreement-feature/${data.user_id}/${data.feature_id}`, data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getListTagsClickFunnels(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/click-funnels/tags/${data.company_id}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getCampaignTagsClickFunnels(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/click-funnels/campaign/tags/${data.campaign_id}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    createTagsClickFunnels(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .post('/click-funnels/tags', data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getListCustomFields(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get('/get-custom-fields')
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getCustomFieldsBySlug(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/get-custom-fields/${data.slug}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getStatusKeywordToTags(context, params) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get('/leadspeek/keyword-to-tags', {
            params
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getStatusOnBoarding(context, params) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/user/onboarding-status/${params.user_id}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    async getDocumentAgreementFile(context, params) {
      try {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
    
        const response = await axios.get('/document/agreement', {
          params,
        });
    
        return response.data;
      } catch (error) {
        if (error.response && error.response.data) {
          throw error.response.data;
        } else {
          throw error;
        }
      }
    },
    getCustomIntegrations(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        if(!data.company_id){
          return reject('error')
        }

        axios
          .get(`/integrations/${data.company_id}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    getCustomIntegrationsDetail(context, { data }) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        if(!data.company_id || !data.slug){
          return reject('error')
        }

        axios
          .get(`/integrations/${data.company_id}/${data.slug}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    couponCode(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .post(`/configuration/coupon/get-coupon`, data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    async getMinSpendLatest(context, params) {
      try {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;

        const response = await axios.get(`/agreement-minimum-spend-latest/${params.company_id}`);
    
        return response.data;
      } catch (error) {
        if (error.response && error.response.data) {
          throw error.response.data;
        } else {
          throw error;
        }
      }
    },
     GetAllCampaigns(context, data) {
    
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + context.state.token;
        axios
          .get(
            `/campaigns/${data.companyID}/${data.groupCompanyID ? data.groupCompanyID : ''}`,
          )
          .then(response => {
            //context.commit('putResultData',response.data)
            //resolve('success')
            resolve(response.data);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    SimplifiCreateCampaign(context, data) {      
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .post('/leadspeek/simplifi', data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    SimplifiUpdateCampaign(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        if (!data.campaignId){
          return reject('error')
        }
        axios
          .put(`/leadspeek/simplifi/${data.campaignId}`, data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    // GET GEO FENCES
    SimplifiGetGeoFences(context, campaignId) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/leadspeek/simplifi/${campaignId}/geo-fences`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data || error);
          });
      });
    },
    // ADD GEO FENCES
    // GET GEO FENCE RECENCY OPTIONS
    SimplifiGetGeoFenceRecencyOptions(context) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get('/leadspeek/simplifi/geo-fence-recency-options')
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data || error);
          });
      });
    },
    // GET DEVICE TYPES
    SimplifiGetDeviceTypes(context) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get('/leadspeek/simplifi/device-types')
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data || error);
          });
      });
    },
    // CONVERSION SEGMENTS
    getSimplifiConversionTypes(context) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get('/leadspeek/simplifi/conversion-types')
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data || error);
          });
      });
    },
    getSimplifiConversionSegment(context, segmentId) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/leadspeek/simplifi/conversion-segment/${segmentId}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data || error);
          });
      });
    },
    getCampaignConversionSegments(context, campaignId) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .get(`/leadspeek/simplifi/${campaignId}/conversion-segments`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data || error);
          });
      });
    },
    applyConversionSegmentsToCampaign(context, { campaignId, data }) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .put(`/leadspeek/simplifi/${campaignId}/conversion-segments`, data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data || error);
          });
      });
    },
    SimplifiAudienceUpload(context, data) {      
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        axios
          .post('/leadspeek/simplifi/audience/upload', data)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    SimplifiGetCampaignStats(context, { data }) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        if(!data.campaign_ids){
          return reject('error')
        }
        axios
          .get(`/leadspeek/simplifi/stats`,{
            params:{
              campaign_ids: data.campaign_ids 
            }
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    SimplifiCampaignReport(context, { data }) {      
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {        
        if(!data.campaign_id || !data.client_now_date || !data.date_range){
          return reject('error')
        }
        const params = {
            campaign_id: data.campaign_id, 
            client_now_date: data.client_now_date, 
            date_range: data.date_range, 
        }
        if (data.start_date) {
          params.start_date = data.start_date
        }
        if (data.end_date) {
          params.end_date = data.end_date
        }
        axios
          .get(`/leadspeek/simplifi/campaign-reports/${data.campaign_id}`,{
            params:params
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    SimplifiGetCampaignDetails(context, { data }) {      
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        if(!data.campaign_id){
          return reject('error')
        }
        const params = {
            campaign_id: data.campaign_id, 
            client_now_date: data.client_now_date, 
            date_range: data.date_range, 
        }
        if (data.start_date) {
          params.start_date = data.start_date
        }
        if (data.end_date) {
          params.end_date = data.end_date
        }
        axios
          .get(`/leadspeek/simplifi/details`,{
            params:params
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    SimplifiGetAudience(context, { data }) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        if(!data.user_id){
          return reject('error')
        }
        axios
          .get(`/leadspeek/simplifi/audience/${data.user_id}`,{
            params:{
              selected_campaign_audience: data.selected_campaign_audience 
            }
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    SimplifiRemoveAudience(context, data) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        if(!data.audience_id){
          return reject('error')
        }
        axios
          .delete(`/leadspeek/simplifi/audience/${data.audience_id}`)
          .then(response => {            
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    SimplifiGetMedia(context, { data }) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        if(!data.user_id){
          return reject('error')
        }
        axios
          .get(`/media/user/${data.user_id}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    GetDownloadMediaToken(context, { data }) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        if(!data.media_ids){
          return reject('error')
        }
        axios
          .get(`/generate-download-token/${data.media_ids}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    SimplifiGetCampaignMedia(context, { data }) {
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        if(!data.campaign_id){
          return reject('error')
        }
        axios
          .get(`/media/campaign/${data.campaign_id}`)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
    UpdateMedia(context, { data }) {      
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + context.state.token;
      return new Promise((resolve, reject) => {
        if(!data.media_id){
          return reject('error')
        }
        axios
          .put(`/media/${data.media_id}`,data.param || {})
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error.response.data);
          });
      });
    },
  }
});

// On page load: check if token needs immediate refresh, then start auto-refresh timer
// This handles: page reload, app opened from bookmark, browser restored after sleep
if (store.getters.loggedIn) {
  checkAndRefreshToken();
}
